// Configuration object for ProductChat
const CHAT_CONFIG = {
  delays: {
    cooldown: 3000,
    retry: 1000,
    typewriter: 10,
    openAIInterval: 20000,
    geminiInterval: 1000
  },
  limits: {
    maxRetries: 3,
    maxFailures: 3
  },
  selectors: {
    description: [
      '.product-description',
      '.product__description',
      '.product-single__description',
      '[data-product-description]',
      '#ProductDescription',
      '.description',
      '.detail',
      '[data-product-detail]',
      '.product__detail',
      '.product-detail'
    ],
    details: [
      '.product-details',
      '.product__details',
      '.product-features',
      '[data-product-details]',
      '#ProductDetails',
      '.detail',
      '[data-product-detail]',
      '.product__detail',
      '.product-detail'
    ],
    specifications: [
      '.product-specifications',
      '.product__specifications',
      '.product-specs',
      '[data-product-specifications]',
      '#ProductSpecifications',
      '.specifications',
      '[data-specifications]',
      '.specs',
      '.product__specs'
    ],
    // Add selectors for Add to Cart buttons
    addToCartButtons: [
      'button[name="add"]', 
      '.add-to-cart', 
      '.add_to_cart', 
      '#AddToCart', 
      '.product-form__cart-submit',
      '[data-action="add-to-cart"]',
      '[aria-label="Add to cart"]'
    ]
  },
  tracking: {
    enabled: true,
    sessionTimeout: 5 * 60 * 1000, // 5 minutes in milliseconds
    sheetUrl: 'https://script.google.com/macros/s/AKfycbzlSZD4EgbEeUA3GX1PPwiIekGG5smEEAa7jEjSoKaJWq7FgJL4NMNx6Fhr4w6699eZ/exec',
    localStoragePrefix: 'productChat_',
    conversionTracking: {
      enabled: true,        // Master switch for conversion tracking
      testMode: false,       // When true, only logs but doesn't affect real data
      attributionWindow: 7, // Days to attribute a conversion to a chat
      storageKey: 'productChat_conversion_data',
      conversionType: 'add_to_cart' // Changed from "purchase" to "add_to_cart"
    }
  },
  // Secure API proxy configuration
  apiProxy: {
    baseUrl: 'https://sanction-api-proxy-production.up.railway.app', // Railway deployment URL
    endpoints: {
      openai: '/api/openai/chat',
      gemini: '/api/gemini/chat'
    }
  }
};

// Add a global reference to make the session info accessible
window.ProductChatTracking = {
  getSessionData: function() {
    const storageKey = CHAT_CONFIG.tracking.conversionTracking.storageKey;
    return JSON.parse(localStorage.getItem(storageKey) || '{}');
  },
  
  // Add a method to track conversions
  trackConversion: function(productData) {
    try {
      // Get the stored session data from localStorage
      const storageKey = CHAT_CONFIG.tracking.conversionTracking.storageKey;
      const sessionData = JSON.parse(localStorage.getItem(storageKey) || '{}');
      
      // Check if we have any sessions to attribute
      if (Object.keys(sessionData).length === 0) {
        console.log('No chat sessions found to attribute conversion');
        return false;
      }
      
      // Get the current test mode setting
      const testMode = CHAT_CONFIG.tracking.conversionTracking.testMode;
      
      // Process product data
      const productId = productData.product_id || productData.id || '';
      const variantId = productData.variant_id || '';
      const price = productData.price || 0;
      const quantity = productData.quantity || 1;
      
      // Try to get the product title
      const productTitle = productData.product_title || productData.title || '';
      
      if (testMode) {
        console.log(`Processing add to cart for product ID: ${productId}`);
        console.log('Available sessions:', Object.keys(sessionData).length);
        
        // Debug all sessions
        for (const sid in sessionData) {
          const s = sessionData[sid];
          console.log(`Session ${sid}: productId=${s.productId}, hasInteraction=${s.hasInteraction}, expires=${new Date(s.expiresAt).toLocaleString()}`);
        }
      }
      
      let sessionMatchFound = false;
      let conversionFound = false;
      let productChatInteractionFound = false;
      
      // Method to normalize IDs for comparison
      const normalizeId = (id) => String(id || '').trim().toLowerCase();
      const normalizedProductId = normalizeId(productId);
      
      // Conversion attribution: Look for ANY session within the attribution window (7 days)
      // that matches this product and had a chat interaction. This allows us to attribute
      // conversions to chat sessions even if they happen in a later visit.
      for (const sessionId in sessionData) {
        const session = sessionData[sessionId];
        
        // Only include sessions that haven't expired (within attribution window)
        if (session.expiresAt > Date.now()) {
          // Check for exact product match with normalized IDs
          const sessionProductId = normalizeId(session.productId);
          
          // Product matching logic
          const forceMatch = testMode && productId === 'test_product';
          const productMatch = sessionProductId === normalizedProductId;
          
          if (productMatch || forceMatch) {
            // This is the right product, now check for interaction
            if (session.hasInteraction || forceMatch) {
              productChatInteractionFound = true;
              sessionMatchFound = true;
              
              if (testMode) {
                console.log(`Found matching session ${sessionId} for product ${productId} with interaction`);
              }
              
              const conversionData = {
                sessionId: sessionId,
                cartId: 'cart_' + Date.now(), // Instead of orderId
                productId: session.productId || productId,
                productTitle: productTitle || session.productTitle || '',
                variantId: session.variantId || variantId,
                price: price,
                quantity: quantity,
                total: price * quantity,
                conversionType: CHAT_CONFIG.tracking.conversionTracking.conversionType,
                testMode: testMode,
                hasInteraction: session.hasInteraction || false // Pass interaction flag to server
              };
              
              // Only log details in test mode
              if (testMode) {
                console.log('Tracking add to cart conversion for session:', sessionId);
                console.log('Conversion data:', conversionData);
              }
              
              // Use image request approach since it's working
              const img = new Image();
              const url = CHAT_CONFIG.tracking.sheetUrl + 
                        '?payload=' + encodeURIComponent(JSON.stringify(conversionData));
              
              // Send the image request
              img.onload = function() { 
                if (testMode) console.log('Conversion request completed successfully');
              };
              img.onerror = function() { 
                if (testMode) console.log('Conversion tracked successfully');
              };
              img.src = url;
              
              conversionFound = true;
              break; // We found a perfect match, break out
            } else if (testMode) {
              console.log(`Skipping session ${sessionId} - no chat interaction occurred`);
            }
          }
        }
      }
      
      // If no session with the right product and interaction was found, 
      // we still need to track the conversion but mark it as having no interaction
      if (!sessionMatchFound && !testMode) {
        if (testMode) {
          if (!sessionMatchFound) {
            console.log(`No matching session found for product ID: ${productId}`);
          } else if (!productChatInteractionFound) {
            console.log(`Found matching session but no chat interaction occurred for product ID: ${productId}`);
          }
        }
        
        // Track conversion with hasInteraction: false to properly reject it server-side
        const conversionData = {
          sessionId: 'no-session-' + Date.now(),
          cartId: 'cart_' + Date.now(),
          productId: productId,
          productTitle: productTitle || '',
          variantId: variantId,
          price: price,
          quantity: quantity,
          total: price * quantity,
          conversionType: CHAT_CONFIG.tracking.conversionTracking.conversionType,
          testMode: testMode,
          hasInteraction: false // Explicitly mark as no interaction
        };
        
        // Send the conversion data so the server can properly reject it
        const img = new Image();
        const url = CHAT_CONFIG.tracking.sheetUrl + 
                  '?payload=' + encodeURIComponent(JSON.stringify(conversionData));
        
        img.onload = function() { 
          if (testMode) console.log('No-interaction conversion request completed');
        };
        img.onerror = function() { 
          if (testMode) console.log('No-interaction conversion properly rejected');
        };
        img.src = url;
        
        return false;
      }
      
      return conversionFound;
    } catch (error) {
      console.error('Error tracking conversion:', error);
      return false;
    }
  }
};

// Add a function to test conversion tracking from the browser console
window.testConversion = function() {
  console.log('Testing add to cart conversion tracking with force match...');
  
  // Create a test product with special test_product ID to force matching in test mode
  const testProduct = {
    product_id: 'test_product',
    variant_id: 'test-variant',
    price: 99.99,
    quantity: 1,
    test_force_match: true // Flag to force matching with any session
  };
  
  // Track the conversion
  const result = window.ProductChatTracking.trackConversion(testProduct);
  
  console.log('Test conversion result:', result);
  
  if (result) {
    console.log('Test conversion successfully initiated!');
    console.log('Check the Google Sheet in a few moments to see if the test data appears.');
  } else {
    console.log('Test conversion failed - no valid session data found.');
    console.log('Try interacting with the chat first to create a session, then try again.');
  }
  
  return result;
};

// Add a function to test targeted product conversion tracking
window.testProductConversion = function(productId) {
  console.log('Testing targeted product add to cart conversion tracking...');
  
  if (!productId) {
    // Try to get productId from page if not provided
    productId = document.querySelector('.product-chat-container')?.dataset.productId || 
                window.location.pathname.split('/products/')[1]?.split('?')[0];
                
    if (!productId) {
      console.log('No product ID provided and none found on page. Please specify a product ID.');
      return false;
    }
  }
  
  console.log(`Using product ID: ${productId}`);
  
  // Create a test product for the specific product
  const testProduct = {
    product_id: productId,
    variant_id: 'test-variant',
    price: 99.99,
    quantity: 1
  };
  
  // Track the conversion
  const result = window.ProductChatTracking.trackConversion(testProduct);
  
  console.log('Product conversion test result:', result);
  
  if (result) {
    console.log('Product conversion test successfully initiated!');
    console.log('Check the Google Sheet in a few moments to see if the test data appears.');
  } else {
    console.log('Product conversion test failed - no matching session found for product ID:', productId);
    console.log('Try interacting with the chat about this product first, then try again.');
  }
  
  return result;
};

// Add a function to test conversion tracking via direct GET request
window.testDirectConversion = function() {
  console.log('Testing conversion tracking via direct GET request...');
  
  // Get the session data
  const sessionData = window.ProductChatTracking.getSessionData();
  const sessions = Object.keys(sessionData);
  
  if (sessions.length === 0) {
    console.log('No active sessions found. Try interacting with the chat first.');
    return false;
  }
  
  // Use the first session found
  const sessionId = sessions[0];
  const session = sessionData[sessionId];
  
  console.log('Using session:', sessionId);
  console.log('Session data:', session);
  
  // Create test order data
  const orderData = {
    sessionId: sessionId,
    orderId: 'DIRECTTEST_' + Date.now(),
    productId: session.productId || 'test-product',
    variantId: session.variantId || 'test-variant',
    price: 99.99,
    quantity: 1,
    total: 99.99,
    conversionType: 'purchase',
    testMode: true
  };
  
  // Create image URL with payload
  const img = new Image();
  const url = CHAT_CONFIG.tracking.sheetUrl + 
              '?payload=' + encodeURIComponent(JSON.stringify(orderData));
  
  console.log('Making GET request to:', url);
  
  // Send the image request
  img.onload = function() { console.log('GET request completed successfully!'); };
  img.onerror = function() { console.log('GET request completed (error expected)'); };
  img.src = url;
  
  return {
    success: true,
    message: 'Direct GET request sent. Check Google Sheet for results.',
    orderData: orderData
  };
};

// Add a function to debug conversion tracking issues
window.debugConversion = function(targetProductId) {
  console.log('---- SESSION MATCHING DIAGNOSTICS ----');
  
  // Get the session data
  const sessionData = window.ProductChatTracking.getSessionData();
  const sessions = Object.keys(sessionData);
  
  if (sessions.length === 0) {
    console.log('No active sessions found. Try interacting with the chat first.');
    return false;
  }
  
  // Create a test order for diagnosing matching logic
  const testOrder = {
    order_id: 'debug-test-' + Date.now(),
    total_price: 99.99,
    line_items: [
      {
        product_id: targetProductId || 'test_product',
        variant_id: 'test-variant',
        price: 99.99,
        quantity: 1
      }
    ]
  };
  
  console.log('Debug test order:', testOrder);
  console.log('---- SESSION MATCHING DIAGNOSTICS ----');
  console.log('Test mode:', CHAT_CONFIG.tracking.conversionTracking.testMode);
  console.log('Order ID:', testOrder.order_id);
  console.log('Line items count:', testOrder.line_items.length);
  
  // Diagnostics for each line item
  testOrder.line_items.forEach((item, index) => {
    const productId = item.product_id;
    console.log(`Processing line item with product ID: ${productId} (${typeof productId})`);
    
    // Check each session for matching
    sessions.forEach(sessionId => {
      const session = sessionData[sessionId];
      const sessionProductId = session.productId || '';
      const now = Date.now();
      const sessionExpired = session.expiresAt < now;
      const testMode = CHAT_CONFIG.tracking.conversionTracking.testMode;
      const forceMatch = testMode && item.product_id === 'test_product';
      
      // Log detailed comparison for debugging
      console.log(`Checking session ${sessionId} with product ID: ${sessionProductId} (${typeof sessionProductId})`);
      console.log(`  Strict equality (===): ${sessionProductId === productId}`);
      console.log(`  Loose equality (==): ${sessionProductId == productId}`);
      console.log(`  String comparison: ${sessionProductId.toString() === productId.toString()}`);
      console.log(`  Trimmed string comparison: ${sessionProductId.toString().trim() === productId.toString().trim()}`);
      console.log(`  Session expired: ${sessionExpired}`);
      console.log(`  Session expires at: ${new Date(session.expiresAt).toLocaleString()}`);
      
      // Check if this session would match in the actual code
      const wouldMatch = !sessionExpired && (forceMatch || sessionProductId === productId.toString());
      console.log(`  Would this session match in code? ${wouldMatch}`);
    });
  });
  
  return 'Detailed diagnostic complete';
};

class ProductChat {
    constructor() {
      this.chatMessages = document.getElementById('chatMessages');
      this.chatInput = document.getElementById('chatInput');
      this.sendButton = document.getElementById('sendMessage');
      this.suggestedPrompts = document.getElementById('suggestedPrompts');
      this.promptsLoader = document.getElementById('promptsLoader');
      this.promptsWrapper = document.querySelector('.suggested-prompts-wrapper');
      
      this.container = document.querySelector('.product-chat-container');
      this.productType = this.container.dataset.productType;
      this.productTitle = this.container.dataset.productTitle;
      
      // Get product information on initialization
      this.productInfo = this.gatherProductInformation();
      
      // Get product ID and variant ID if available
      this.productId = this.container.dataset.productId || this.getProductIdFromUrl();
      this.variantId = this.container.dataset.variantId || '';
      
      this.isProcessing = false;
      this.cooldownTime = CHAT_CONFIG.delays.cooldown;
      this.lastRequestTime = 0;
      this.countdownInterval = null;
      this.currentMessageId = null;
      this.promptsGenerated = false;
  
      // Add rate limiting properties
      this.retryDelay = CHAT_CONFIG.delays.retry;
      this.maxRetries = CHAT_CONFIG.limits.maxRetries;
      this.retryCount = 0;
      this.lastApiCall = 0;
      this.minApiInterval = CHAT_CONFIG.delays.openAIInterval; // 20 seconds between calls
      
      // Add cache for prompts
      this.promptsCache = new Map();
  
      // API keys now handled securely by backend proxy
  
      // API state tracking
      this.lastOpenAICall = 0;
      this.lastGeminiCall = 0;
      this.openAIMinInterval = CHAT_CONFIG.delays.openAIInterval; // 20 seconds between OpenAI calls
      this.geminiMinInterval = CHAT_CONFIG.delays.geminiInterval;  // 1 second between Gemini calls

      // Service health monitoring
      this.serviceHealth = {
        openai: { failures: 0, lastSuccess: null },
        gemini: { failures: 0, lastSuccess: null }
      };
      this.maxFailures = CHAT_CONFIG.limits.maxFailures; // Switch services after 3 consecutive failures
  
      // Add typewriter configuration
      this.typewriterDelay = CHAT_CONFIG.delays.typewriter; // milliseconds between characters
      
      // Add tracking properties
      this.trackingEnabled = CHAT_CONFIG.tracking.enabled;
      this.sessionId = this.generateSessionId();
      this.sessionStartTime = Date.now();
      this.lastInteractionTime = Date.now();
      this.lastActivityTime = Date.now(); // Track last activity time for idle detection
      this.sessionTimeout = CHAT_CONFIG.tracking.sessionTimeout;
      this.questionCount = 0; // Real questions only
      this.interactionCount = 0; // All interactions including acknowledgements
      this.activeSession = true;
      this.sheetUrl = CHAT_CONFIG.tracking.sheetUrl;
      this.storagePrefix = CHAT_CONFIG.tracking.localStoragePrefix;
      this.finalizedInteractions = new Set(); // Track which interactions have been finalized
      
      // Store tracked interactions separately from finalization data
      this.trackedInteractions = [];
      
      // Add session idle checker
      this.idleCheckInterval = null;
      
      // Clean up outdated finalization markers
      this.cleanupFinalizationMarkers();
      
      // Check for previous session that wasn't properly closed
      this.checkForPreviousSession();
      
      // Initialize conversion tracking
      this.initConversionTracking();
  
      this.initialize();
    }
    
    // Clean up outdated finalization markers
    cleanupFinalizationMarkers() {
      try {
        // Get all localStorage keys
        const keys = [];
        for (let i = 0; i < localStorage.length; i++) {
          keys.push(localStorage.key(i));
        }
        
        // Find all finalization marker keys for this product chat
        const finalizationPrefix = this.storagePrefix + 'finalized_';
        const markerKeys = keys.filter(key => key.startsWith(finalizationPrefix));
        
        const now = Date.now();
        let cleanupCount = 0;
        
        // Check each marker for expiration
        markerKeys.forEach(key => {
          try {
            // Get timestamp from stored value (format: "true|timestamp")
            const markerData = localStorage.getItem(key);
            
            // Parse the timestamp if it exists
            let timestamp = 0;
            if (markerData && markerData.includes('|')) {
              timestamp = parseInt(markerData.split('|')[1], 10);
            }
            
            // Default expiration is 24 hours (in milliseconds)
            const expirationTime = 24 * 60 * 60 * 1000;
            
            // Remove if expired or invalid
            if (!timestamp || (now - timestamp) > expirationTime) {
              localStorage.removeItem(key);
              cleanupCount++;
            }
          } catch (parseError) {
            // If we can't parse, just remove the marker
            localStorage.removeItem(key);
            cleanupCount++;
          }
        });
        
        if (cleanupCount > 0) {
          console.log(`Cleaned up ${cleanupCount} expired finalization markers`);
        }
      } catch (error) {
        console.error('Error cleaning up finalization markers:', error);
        // Don't let cleanup errors stop the initialization
      }
    }
  
    initialize() {
      // Find the parent accordion details element
      const parentAccordion = this.container.closest('.disclosure');
      if (parentAccordion) {
        parentAccordion.addEventListener('toggle', (event) => {
          if (parentAccordion.open && !this.promptsGenerated) {
            this.showLoader();
            this.generateSuggestedPrompts();
            this.promptsGenerated = true;
          }
        });
      }

      this.sendButton.addEventListener('click', () => {
        const message = this.chatInput.value.trim();
        if (message) {
          this.showChatMessages();
          this.sendMessage(message);
          this.chatInput.value = '';
          this.updateLastActivityTime();
        }
      });
  
      this.chatInput.addEventListener('keypress', (event) => {
        if (event.key === 'Enter') {
          const message = this.chatInput.value.trim();
          if (message) {
            this.showChatMessages();
            this.sendMessage(message);
            this.chatInput.value = '';
            this.updateLastActivityTime();
          }
        }
      });
      
      // Track user activity
      this.chatInput.addEventListener('input', () => this.updateLastActivityTime());
      this.chatInput.addEventListener('focus', () => this.updateLastActivityTime());
      this.chatInput.addEventListener('click', () => this.updateLastActivityTime());
      this.container.addEventListener('click', () => this.updateLastActivityTime());
      document.addEventListener('mousemove', () => this.updateLastActivityTime());
      document.addEventListener('keydown', () => this.updateLastActivityTime());
      
      // Start idle checker
      this.startIdleChecker();
      
      // Add beforeunload handler to log session end
      window.addEventListener('beforeunload', () => {
        // Stop idle checker
        this.stopIdleChecker();
        
        // Save current session state to localStorage before page unloads
        if (this.trackingEnabled && this.activeSession) {
          this.saveSessionToLocalStorage();
        }
        this.checkAndFinalizeSession(true);
      });
      
      // Add page visibility change handler to detect tab switching/closing
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          // Save session state when page is hidden
          if (this.trackingEnabled && this.activeSession) {
            this.saveSessionToLocalStorage();
          }
        } else {
          // Page is visible again, update activity time
          this.updateLastActivityTime();
        }
      });
    }
  
    showLoader() {
      this.promptsWrapper.style.display = 'block';
      this.promptsLoader.style.display = 'flex';
      
      // Force a reflow and set initial height
      const height = this.promptsLoader.offsetHeight;
      this.promptsWrapper.style.height = height + 'px';
    }
  
    hideLoader() {
      this.promptsLoader.style.display = 'none';
    }
  
    showPrompts() {
      // First make the wrapper and prompts visible but with 0 opacity
      this.promptsWrapper.style.display = 'block';
      this.suggestedPrompts.style.display = 'flex';
      this.suggestedPrompts.style.opacity = '0';
      
      // Force a reflow to get the natural height
      this.suggestedPrompts.offsetHeight;
      
      // Set the wrapper height to match the content
      const height = this.suggestedPrompts.offsetHeight;
      this.promptsWrapper.style.height = height + 'px';
      
      // Now fade in the prompts
      requestAnimationFrame(() => {
        this.suggestedPrompts.style.opacity = '1';
      });
    }
  
    getFallbackPrompts() {
      return [
        "What are the features?",
        "How's the sizing?",
        "What materials?",
        "Care instructions?",
        "Is this right for me?"
      ];
    }
  
    async generateSuggestedPrompts() {
      try {
        // Check cache first
        const cacheKey = `${this.productType}-${this.productTitle}`;
        if (this.promptsCache.has(cacheKey)) {
          const cachedPrompts = this.promptsCache.get(cacheKey);
          this.hideLoader();
          this.renderPrompts(cachedPrompts);
          this.showPrompts();
          return;
        }
  
        // Check if we need to wait before making another API call
        const now = Date.now();
        const timeSinceLastCall = now - this.lastApiCall;
        if (timeSinceLastCall < this.minApiInterval) {
          console.log('Too soon for API call, using fallback');
          const fallbackPrompts = this.getFallbackPrompts();
          this.hideLoader();
          this.renderPrompts(fallbackPrompts);
          this.showPrompts();
          return;
        }
  
        // Try OpenAI first
        const openAiPrompts = await this.tryOpenAI();
        if (openAiPrompts) {
          this.promptsCache.set(cacheKey, openAiPrompts);
          this.hideLoader();
          this.renderPrompts(openAiPrompts);
          this.showPrompts();
          return;
        }
  
        // If OpenAI fails, try Gemini
        const geminiPrompts = await this.tryGemini();
        if (geminiPrompts) {
          this.promptsCache.set(cacheKey, geminiPrompts);
          this.hideLoader();
          this.renderPrompts(geminiPrompts);
          this.showPrompts();
          return;
        }
  
        // If both fail, use fallback prompts
        const fallbackPrompts = this.getFallbackPrompts();
        this.hideLoader();
        this.renderPrompts(fallbackPrompts);
        this.showPrompts();
      } catch (error) {
        console.error('Error generating prompts:', error);
        const fallbackPrompts = this.getFallbackPrompts();
        this.hideLoader();
        this.renderPrompts(fallbackPrompts);
        this.showPrompts();
      }
    }
  
    async tryOpenAI() {
      try {
        const systemPrompt = `You are a retail expert. Generate 5 short, focused questions about this product. 
        IMPORTANT: 
        - When determining what the product is, consider the product title and any description in the div with class .detail.
        - Return ONLY the questions, one per line
        - Do NOT include the product name in the questions
        - Do NOT use any prefixes, numbers, dashes, or bullets
        - Keep questions short and direct
        - Focus on features, sizing, materials, usage, and care`;

        const response = await fetch(`${CHAT_CONFIG.apiProxy.baseUrl}${CHAT_CONFIG.apiProxy.endpoints.openai}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            model: "gpt-4o-mini",
            messages: [{
              role: "system",
              content: systemPrompt
            }, {
              role: "user",
              content: `Generate 5 concise questions about this ${this.productType}: ${this.productTitle}. Do not include the product name in the questions.`
            }]
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          console.error('OpenAI API Error:', errorData);
          return null;
        }

        const proxyResponse = await response.json();
        
        // Handle proxy response structure
        if (!proxyResponse.success) {
          console.error('OpenAI Proxy Error:', proxyResponse.error);
          return null;
        }
        
        const data = proxyResponse.data;
        if (!data.choices?.[0]?.message?.content) {
          return null;
        }

        return data.choices[0].message.content
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0)
          .map(line => line.replace(/^[-–—•*+]\s*/, ''))
          .filter(line => !line.match(/^\d+[\.\)-]/))
          .slice(0, 5);
      } catch (error) {
        console.error('OpenAI Error:', error);
        return null;
      }
    }
  
    async tryGemini() {
      try {
        const prompt = `As a young, friendly skater/snowboarder who works at Sanction skateshop, generate 5 short, focused questions about this ${this.productType}: ${this.productTitle}.
        You've been working at the shop for years and know everything about skateboarding, snowboarding, and the gear. You're passionate about the sports and love helping customers find the perfect setup. You have a casual, laid-back vibe but are super knowledgeable and professional when it comes to gear.

        IMPORTANT: 
        - When determining what the product is, consider the product title and any description in the div with class .detail
        - Return ONLY the questions, one per line
        - Do NOT include the product name in the questions
        - Do NOT use any prefixes, numbers, dashes, or bullets
        - Keep questions short and direct
        - Focus on features, sizing, materials, usage, and care
        - Use casual but professional language - think "stoked", "sick", "rad", "bombproof", etc. but don't overdo it
        - Share your personal experience with the product when relevant
        - Be enthusiastic but authentic`;
  
        const response = await fetch(`${CHAT_CONFIG.apiProxy.baseUrl}${CHAT_CONFIG.apiProxy.endpoints.gemini}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            contents: [{
              parts: [{
                text: prompt
              }]
            }]
          })
        });
  
        console.log('Gemini Status:', response.status);
  
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.log('Gemini error details:', errorData);
          return null;
        }
  
        const proxyResponse = await response.json();
        
        // Handle proxy response structure
        if (!proxyResponse.success) {
          console.error('Gemini Proxy Error:', proxyResponse.error);
          return null;
        }
        
        const data = proxyResponse.data;
        console.log('Gemini response:', data);
  
        if (!data.candidates?.[0]?.content?.parts?.[0]?.text) {
          console.log('Gemini invalid response format:', data);
          return null;
        }
  
        return data.candidates[0].content.parts[0].text
          .split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0)
          .map(line => line.replace(/^[-–—•*+]\s*/, ''))
          .filter(line => !line.match(/^\d+[\.\)-]/))
          .slice(0, 5);
      } catch (error) {
        console.error('Gemini Error:', error);
        return null;
      }
    }
  
    renderPrompts(prompts) {
      const cleanPrompts = prompts
        .filter(prompt => prompt && prompt.trim())
        .map(prompt => prompt.replace(/^[-–—•*+]\s*/, '').trim()) // Remove any remaining dashes or bullets
        .filter(prompt => prompt.length > 0);
  
      this.suggestedPrompts.innerHTML = cleanPrompts
        .map(prompt => {
          // Just use the prompt directly without adding product name
          return `
            <button class="prompt-button" data-prompt="${prompt.replace(/"/g, '&quot;')}">
              ${prompt}
            </button>
          `;
        }).join('');
  
      // Add event listeners and trigger animations
      const buttons = this.suggestedPrompts.querySelectorAll('.prompt-button');
      buttons.forEach((button, index) => {
        button.addEventListener('click', () => {
          const prompt = button.dataset.prompt;
          this.showChatMessages();
          this.sendMessage(prompt);
          
          // Check if we're on a mobile device (screen width less than 768px)
          if (window.innerWidth < 768) {
            // Ensure the chat window is visible, especially on mobile
            setTimeout(() => {
              // Calculate a more appropriate scroll position that shows just the beginning of the chat window
              const chatRect = this.chatMessages.getBoundingClientRect();
              const scrollPosition = window.pageYOffset + chatRect.top - 100; // Show more context above
              
              // Use a gentler scroll
              window.scrollTo({
                top: scrollPosition,
                behavior: 'smooth' 
              });
            }, 300); // Slightly longer delay to ensure messages have started loading
          }
        });
        
        // Trigger the animation after a brief delay
        requestAnimationFrame(() => {
          button.classList.add('show');
        });
      });
    }
  
    showChatMessages() {
      if (this.chatMessages.style.display === 'none') {
        this.chatMessages.style.display = 'block';
        this.chatMessages.scrollTop = this.chatMessages.scrollHeight;
      }
    }
  
    clearCurrentMessage() {
      if (this.currentMessageId) {
        this.removeMessage(this.currentMessageId);
        this.currentMessageId = null;
      }
    }
  
    startCountdown(seconds) {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }
      this.clearCurrentMessage();
  
      this.currentMessageId = Date.now();
      this.addMessageToChat('assistant', `Please wait ${seconds} seconds before sending another message.`, this.currentMessageId);
      
      let remainingSeconds = seconds;
      this.countdownInterval = setInterval(() => {
        remainingSeconds--;
        
        if (remainingSeconds <= 0) {
          clearInterval(this.countdownInterval);
          this.clearCurrentMessage();
          this.currentMessageId = Date.now();
          this.addMessageToChat('assistant', 'You can now send another message.', this.currentMessageId)
            .then(() => setTimeout(() => this.clearCurrentMessage(), 2000));
        } else {
          const messageElement = this.chatMessages.querySelector(`[data-message-id="${this.currentMessageId}"]`);
          if (messageElement) {
            messageElement.textContent = `Please wait ${remainingSeconds} seconds before sending another message.`;
          }
        }
      }, 1000);
    }
  
    // Add method to detect acknowledgements
    isAcknowledgement(message) {
      const normalizedMessage = message.toLowerCase().trim();
      
      // Common acknowledgement patterns
      const acknowledgementPatterns = [
        /^thanks?/i,
        /^thank you/i,
        /^cool/i,
        /^sounds? good/i,
        /^nice/i,
        /^sweet/i,
        /^rad/i,
        /^awesome/i,
        /^perfect/i,
        /^got it/i,
        /^okay?/i,
        /^ok/i,
        /^great/i,
        /^sick/i,
        /^dope/i,
        /^stoked/i,
        /^gnarly/i,
        /^buttery/i,
        /^epic/i,
        /^fire/i,
        /^lit/i,
        /^tight/i,
        /^legit/i,
        /^solid/i,
        /^bet/i,
        /^word/i,
        /^yup/i,
        /^yeah/i,
        /^yep/i,
        /^sure/i,
        /^gotcha/i,
        /^roger/i,
        /^copy/i,
        /^10-4/i,
        /^👍/i,
        /^👌/i,
        /^🙌/i,
        /^🤙/i,
        /^✌️/i
      ];

      // Check if message matches any acknowledgement pattern
      return acknowledgementPatterns.some(pattern => pattern.test(normalizedMessage));
    }

    // Add method to get a random closing message
    getRandomClosingMessage() {
      const closingMessages = [
        "No problem! Let me know if you have any other questions.",
        "Stoked I could help. Hit me up if you need anything else!",
        "All good, dude. Happy shredding!",
        "Rad! Holler if you need anything else.",
        "Sweet! Catch you later!",
        "Awesome! Let me know if you need anything else.",
        "Perfect! Happy riding!",
        "Got it! Hit me up if you have more questions.",
        "Sick! Let me know if you need anything else.",
        "Dope! Happy shredding!",
        "Epic! Catch you later!",
        "Fire! Let me know if you need anything else.",
        "Lit! Happy riding!",
        "Tight! Holler if you need anything else.",
        "Legit! Catch you later!",
        "Solid! Let me know if you need anything else.",
        "Bet! Happy shredding!",
        "Word! Hit me up if you have more questions.",
        "Yup! Let me know if you need anything else.",
        "Yeah! Happy riding!"
      ];

      return closingMessages[Math.floor(Math.random() * closingMessages.length)];
    }

    // Add method to detect return queries (separate from warranty)
    isReturnQuery(message) {
      const normalizedMessage = message.toLowerCase();

      // Return-specific patterns
      const returnPatterns = [
        /\breturn policy\b/,
        /\breturns policy\b/,
        /\bcan.*return\b/,
        /\bhow.*return\b/,
        /\breturn.*process\b/,
        /\breturn.*procedure\b/,
        /\brefund policy\b/,
        /\bexchange policy\b/,
        /\breturn.*window\b/,
        /\breturn.*period\b/,
        /\breturn.*timeframe\b/,
        /\breturn.*time\b/,
        /\bcan.*i.*return\b/,
        /\bhow.*long.*return\b/,
        /\bwhen.*can.*return\b/,
        /\breturn.*if\b/,
        /\bchange.*mind\b/,
        /\bdon't.*like\b/,
        /\bdoesn't.*fit\b/,
        /\bwrong.*size\b/,
        /\bnot.*what.*expected\b/
      ];

      return returnPatterns.some(pattern => pattern.test(normalizedMessage));
    }

    // Add method to detect shipping queries
    isShippingQuery(message) {
      const normalizedMessage = message.toLowerCase();

      // Shipping-specific patterns
      const shippingPatterns = [
        /\bshipp(ing|ed)?\b/,
        /\bdeliver(y|ies|ed)?\b/,
        /\bfree shipping\b/,
        /\bshipping cost\b/,
        /\bshipping price\b/,
        /\bshipping fee\b/,
        /\bhow much.*ship\b/,
        /\bship.*international\b/,
        /\binternational.*ship\b/,
        /\bship.*to\b/,
        /\bship.*canada\b/,
        /\bship.*us\b/,
        /\bship.*usa\b/,
        /\bhow long.*ship\b/,
        /\bwhen.*arrive\b/,
        /\bwhen.*get.*it\b/,
        /\bshipping.*time\b/,
        /\bdelivery.*time\b/,
        /\btrack.*order\b/,
        /\btracking\b/
      ];

      return shippingPatterns.some(pattern => pattern.test(normalizedMessage));
    }

    // Get shipping policy response
    getShippingResponse() {
      return `<p>We ship internationally! 🌍</p>
<p><strong>Free shipping on orders over $99</strong> — but heads up, that's for Canadian orders only right now.</p>
<p>For international orders, shipping costs will be calculated at checkout based on your location. We'll get your gear to you as fast as possible!</p>
<p>Need more details? Check out our <a href="https://sanctionsnow.com/pages/shipping-terms-and-taxes">Shipping FAQ</a> for all the info. Stay stoked!</p>`;
    }

    // Add method to detect warranty queries (separate from returns)
    isWarrantyOrSupportQuery(message) {
      const normalizedMessage = message.toLowerCase();

      // First check if it's a general warranty question (excluding return-related terms)
      const generalWarrantyPatterns = [
        /\bwarranty\b/,
        /\bmoney.*back.*guarantee\b/,
        /\bguarantee period\b/,
        /\bhow.*long.*warranty\b/,
        /\bwarranty.*coverage\b/,
        /\bwarranty.*period\b/,
        /\bmanufacturer.*warranty\b/,
        /\bwarranty.*claim\b/,
        /\bunder.*warranty\b/,
        /\bwarranty.*info\b/,
        /\bwarranty.*information\b/
      ];

      // If it matches a general warranty question pattern, return 'info'
      if (generalWarrantyPatterns.some(pattern => pattern.test(normalizedMessage))) {
        return 'info';
      }

      // Keywords that indicate an actual warranty claim or support need
      const supportKeywords = [
        /\bbroken\b/,
        /\bdamaged?\b/,
        /\bdefective\b/,
        /\bnot working\b/,
        /\bstopped working\b/,
        /\bbroke\b.*\boff\b/,
        /\bdefect\b/,
        /\brepair\b/,
        /\breplace\b.*\bbroken\b/,
        /\breplacement\b.*\bbroken\b/,
        /\bfaulty\b/,
        /\bmalfunction\b/,
        /\bsnapped\b/,
        /\bcracked\b/,
        /\btorn\b/
      ];

      // Check for actual support needs
      const needsSupport = supportKeywords.some(pattern => pattern.test(normalizedMessage));
      if (needsSupport) {
        // Look for past tense or ownership indicators that suggest they already have the product
        const ownershipIndicators = [
          /\bmy\b/,
          /\bmine\b/,
          /\bi have\b/,
          /\bi got\b/,
          /\bi bought\b/,
          /\bi purchased\b/,
          /\bi received\b/,
          /\barrived\b.*\bbroken\b/,
          /\bcame\b.*\bbroken\b/
        ];
        
        // If they have the product and need support, return 'claim'
        if (ownershipIndicators.some(pattern => pattern.test(normalizedMessage))) {
          return 'claim';
        }
      }

      // If no patterns matched, return false
      return false;
    }

    // Get return policy response
    getReturnPolicyResponse() {
      return `<p>No restocking fee, no hassles! Returns can be made on regular priced items within 14 days of purchase with a valid R/A number.</p>
<p>Sale items can be returned within 14 days for store credit or exchange only. Check out our full <a href="https://sanctionsnow.com/pages/returns-warranty">Returns & Warranty page</a> for all the details!</p>
<p>Just hit us up for a Return Authorization Number before sending anything back. Stay stoked!</p>`;
    }

    // Get warranty info response
    getWarrantyInfoResponse() {
      return `<p>Totally depends on the brand. Most have some kind of warranty, but it's best to check with the brand directly for the lowdown. We've also got a <a href="https://sanctionsnow.com/pages/returns-warranty">Returns & Warranty page</a> if you wanna peep that too.</p>
<p>Pro tip: hang onto your receipt — just in case. Stay stoked!</p>`;
    }

    // Get warranty claim response
    getWarrantyClaimResponse() {
      return `<p>Ah bummer, sorry to hear that!</p>
<p>Good news though — you can start a warranty claim by heading to our <a href="https://sanctionsnow.com/pages/returns-warranty#warranty-info">Returns & Warranty page</a>.</p>
<p>Just scroll down and click the <strong>"Start A Warranty Claim"</strong> button. We'll get you sorted fast!</p>`;
    }

    // Add these utility methods before the sendMessage method

    async handleServiceResponse(service, message, serviceCall) {
      const now = Date.now();
      console.log(`Trying ${service}...`);
      const response = await serviceCall(message);
      
      if (response && this.isValidResponse(response)) {
        this.serviceHealth[service.toLowerCase()].failures = 0;
        this.serviceHealth[service.toLowerCase()].lastSuccess = now;
        return response;
      } else {
        this.serviceHealth[service.toLowerCase()].failures++;
        return null;
      }
    }

    async tryServices(message) {
      const now = Date.now();
      const openAIAvailable = (now - this.lastOpenAICall) >= this.openAIMinInterval;
      const geminiAvailable = (now - this.lastGeminiCall) >= this.geminiMinInterval;
      const useOpenAI = this.serviceHealth.openai.failures < this.maxFailures;

      // Try OpenAI if available and healthy
      if (useOpenAI && openAIAvailable) {
        const response = await this.handleServiceResponse('OpenAI', message, this.tryOpenAIChat.bind(this));
        if (response) return response;
      }

      // Try Gemini if OpenAI failed or unhealthy
      if (geminiAvailable) {
        const response = await this.handleServiceResponse('Gemini', message, this.tryGeminiChat.bind(this));
        if (response) return response;
      }

      return null;
    }

    async handleFailedResponse() {
      const waitTime = Math.ceil(Math.min(
        this.openAIMinInterval - (Date.now() - this.lastOpenAICall),
        this.geminiMinInterval - (Date.now() - this.lastGeminiCall)
      ) / 1000);
      
      if (waitTime > 0) {
        return `Please wait ${waitTime} seconds before sending another message.`;
      }
      return 'Sorry, I encountered an error. Please try again in a moment.';
    }

    async sendMessage(message) {
      if (this.isProcessing) {
        this.showChatMessages();
        await this.addMessageToChat('assistant', 'Please wait for the previous response to complete.');
        return;
      }

      try {
        this.isProcessing = true;
        this.showChatMessages();
        await this.addMessageToChat('user', message);

        // Check if session is active and reset if needed
        this.checkSession();
        
        // Track interaction type
        const isAck = this.isAcknowledgement(message);
        this.interactionCount++;
        
        // Only count real questions
        if (!isAck) {
          this.questionCount++;
        }

        // Mark that this session has had a chat interaction for conversion tracking
        this.updateSessionInteractionFlag();

        // Handle acknowledgements
        if (isAck) {
          const response = this.getRandomClosingMessage();
          await this.addMessageToChat('assistant', response);
          
          // Log the interaction
          if (this.trackingEnabled) {
            this.trackInteraction(message, response, false);
          }
          
          return;
        }

        // Handle shipping queries
        if (this.isShippingQuery(message)) {
          const response = this.getShippingResponse();
          await this.addMessageToChat('assistant', response);
          
          // Log the interaction
          if (this.trackingEnabled) {
            this.trackInteraction(message, response, false);
          }
          
          return;
        }

        // Handle return policy queries (separate from warranty)
        if (this.isReturnQuery(message)) {
          const response = this.getReturnPolicyResponse();
          await this.addMessageToChat('assistant', response);
          
          // Log the interaction
          if (this.trackingEnabled) {
            this.trackInteraction(message, response, false);
          }
          
          return;
        }

        // Handle warranty queries
        const warrantyType = this.isWarrantyOrSupportQuery(message);
        if (warrantyType === 'info') {
          const response = this.getWarrantyInfoResponse();
          await this.addMessageToChat('assistant', response);
          
          // Log the interaction
          if (this.trackingEnabled) {
            this.trackInteraction(message, response, false);
          }
          
          return;
        } else if (warrantyType === 'claim') {
          const response = this.getWarrantyClaimResponse();
          await this.addMessageToChat('assistant', response);
          
          // Log the interaction
          if (this.trackingEnabled) {
            this.trackInteraction(message, response, false);
          }
          
          return;
        }

        await this.addMessageToChat('assistant', 'Thinking...');

        let response = null;
        let retryCount = 0;
        const maxRetries = CHAT_CONFIG.limits.maxRetries;
        const baseDelay = CHAT_CONFIG.delays.retry;

        while (retryCount <= maxRetries) {
          response = await this.tryServices(message);
          
          if (response) break;

          retryCount++;
          if (retryCount <= maxRetries) {
            const delay = baseDelay * Math.pow(2, retryCount);
            console.log(`Retry ${retryCount} after ${delay}ms`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        }

        // Remove "Thinking..." message
        this.chatMessages.lastElementChild.remove();
        
        if (response) {
          await this.addMessageToChat('assistant', response);
          
          // Track the interaction (not log directly)
          if (this.trackingEnabled) {
            this.trackInteraction(message, response, false);
          }
          
          // Show the AI disclaimer
          const disclaimer = document.querySelector('.ai-disclaimer');
          if (disclaimer) {
            disclaimer.classList.add('visible');
          }
        } else {
          const errorMessage = await this.handleFailedResponse();
          await this.addMessageToChat('assistant', errorMessage);
          
          // Track the error interaction
          if (this.trackingEnabled) {
            this.trackInteraction(message, errorMessage, false);
          }
        }
      } catch (error) {
        console.error('Chat Error:', error);
        this.chatMessages.lastElementChild?.remove();
        const errorMessage = 'Sorry, I encountered an error. Please try again.';
        await this.addMessageToChat('assistant', errorMessage);
        
        // Track the error interaction
        if (this.trackingEnabled) {
          this.trackInteraction(message, errorMessage, false);
        }
      } finally {
        this.isProcessing = false;
      }
    }
  
    async tryOpenAIChat(message) {
      try {
        // Check if this is a warranty-related query (but not a return query)
        const warrantyType = !this.isReturnQuery(message) ? this.isWarrantyOrSupportQuery(message) : false;

        // Check if the question is about sizing and if sizing specs are available
        const isSizeQuestion = message.toLowerCase().includes('size') || 
                             message.toLowerCase().includes('fit') || 
                             message.toLowerCase().includes('measurement');
        const hasSizingSpecs = this.productInfo.details?.includes('Sizing Specs');
        
        // Check if this is a truck sizing question
        const isTruckSizingQuestion = message.toLowerCase().includes('truck') && 
                                    (message.toLowerCase().includes('size') || 
                                     message.toLowerCase().includes('width') || 
                                     message.toLowerCase().includes('fit') ||
                                     message.toLowerCase().includes('choose') ||
                                     message.toLowerCase().includes('recommend'));
        
        const systemPrompt = `You are a young, friendly skater/snowboarder who works at Sanction skateshop. You're super passionate about riding and love helping people find the perfect setup. You've been skating/riding for years and know all the gear inside out. You have a casual, fun personality and love sharing your stoke for the sport.

        Here is the available product information:
        ${this.productInfo.description ? `\nDescription: ${this.productInfo.description}` : ''}
        ${this.productInfo.details ? `\nDetails: ${this.productInfo.details}` : ''}
        ${this.productInfo.specifications ? `\nSpecifications: ${this.productInfo.specifications}` : ''}
        
        ${isSizeQuestion && hasSizingSpecs ? `\nIMPORTANT: The user has asked about sizing, and there is a "Sizing Specs" section available on the product page. Direct them to check the "Sizing Specs" section just below this chat window for detailed measurements and fit information.` : ''}
        
        ${isTruckSizingQuestion ? `
        SKATEBOARD TRUCK SIZING GUIDANCE:
        
        When customers ask which skateboard trucks they should get, help them choose the right skateboard truck size based on deck width and truck brand.
        
        Rules:
        1. If the user is on a skateboard truck product page (or the product name includes "truck"):
           • Check the product name to identify the brand.
           • Return sizing guidance only for the matching brand.
        
        2. If the user is on a skateboard deck or wheels product page and asks about truck sizing:
           • Return the full sizing chart for all brands.
           • If they mention a specific truck brand in the question, return only that brand's sizing.
        
        3. If the brand cannot be determined:
           • Ask the user for their deck width and preferred truck brand before making a recommendation.
        
        Known truck brands and sizing guidelines:
        
        Independent:
        • 7.4"-7.8": 129
        • 7.8"-8.2": 139
        • 8.2"-8.375": 144
        • 8.375"-8.6": 149
        • 8.6"-9.0": 159
        • 9.0"-9.5": 169
        • 9.5"-10.5"+: 215
        
        Thunder:
        • 7.4"-7.9": 145
        • 7.9"-8.12": 147
        • 8.12"-8.38": 148
        • 8.38"-8.62": 149
        • 8.62"-8.85": 151
        • 8.9"+: 161
        • 9.5"+: 181
        
        Ace AF1:
        • 7.25"-8.0": 22
        • 8.0"-8.25": 33
        • 8.25"-8.5": 44
        • 8.5"-8.75": 55
        • 8.75"-9.0": 60
        • 9.0"-9.25": 66
        • 9.25"+: 77
        • 10.0"+: 80
        
        Ace Classic:
        • 7.5"-7.875": 22
        • 7.875"-8.25": 33
        • 8.25"-8.75": 44
        • 8.75"-9.125": 55
        • 9.125"-9.75": 66
        
        Venture:
        • 7.9"-8.1": 5.2
        • 8.1"-8.3": 5.6
        • 8.4"-8.6": 5.8
        • 8.62"+: 6.1
        
        Lurpiv:
        • 7.75"-8.125": 140
        • 8.25"-8.5": 145
        • 8.5"-8.75": 150
        • 8.75"+: 160
        
        Slappy:
        • 7.5"-8.0": 7.75"
        • 7.75"-8.25": 8"
        • 8.0"-8.5": 8.25"
        • 8.25"-8.75": 8.5"
        • 8.5"-9.0": 8.75"
        • 9.5"-10.5": 10"
        • 10.5"-11.5": 11"
        
        Royal:
        • 7.75"-8.25": 139
        • 8.0"-8.5": 144
        • 8.25"-8.75": 149
        • 8.5"+: 159
        ` : ''}
        
        CRITICAL RULES - DO NOT BREAK THESE:
        - ABSOLUTELY NEVER mention the product name, brand, or model in your response
        - NEVER use phrases like "this product", "this item", "these shoes", "this deck", etc.
        - NEVER start with "The [product name] is..." or similar constructions
        - ALWAYS talk about features and details directly without any product references
        - NEVER use any greeting-like language (no "Hey", "Hi", "Yo", "What's up", etc.)
        - NEVER address the user directly
        - Example of what NOT to do:
          ❌ "The New Balance Numeric Reynolds 933 Black Olive is available in sizes 9 and 12"
          ❌ "This deck has a perfect concave for street riding"
          ❌ "These bindings are super responsive"
          ❌ "Hey there! Let me tell you about this product..."
          ❌ "Yo! I've been riding these for years..."
        - Example of what to do:
          ✅ "Available in sizes 9 and 12 - perfect for getting that dialed fit!"
          ✅ "The concave is perfect for street riding - you'll be popping ollies like butter!"
          ✅ "Super responsive bindings that'll have you carving like a dream"
        
        OTHER IMPORTANT RULES:
        - Keep responses short and to the point - aim for 2-3 sentences max
        - Use skate/snowboard slang naturally: "stoked", "rad", "send it", "cruise", "gnarly", "buttery", "pop", "shred", etc.
        - Use emojis VERY occasionally - only when it feels natural and adds to the message
        - DO NOT use emojis in every response
        - Share your genuine excitement about riding and the gear
        - Make the customer feel stoked to ride
        - Keep it fun and energetic but brief
        - Use the product info to give helpful, accurate advice
        - Share your personal experience when relevant
        - Throw in a quick fun comment or joke sometimes
        - Avoid any corporate or formal language
        - Stay positive and enthusiastic
        - Make the customer feel welcome and excited
        - DO NOT start responses with greetings like "Hey" or "Hi"
        
        SPECIFIC PRODUCT KNOWLEDGE:
        - SKATEBOARD TRUCKS: Always sold in pairs (set of 2). If someone asks if they're sold individually or as pairs, always clarify they come as a pair.
        - SKATEBOARD WHEELS: Always sold as a set of four (4). If someone asks about wheel quantities, pricing, or individual wheels, always clarify that wheels come as a set of 4.
        - SNOWBOARD BINDINGS: Always sold as a set of two (2). If someone asks about binding quantities, pricing, or individual bindings, always clarify that bindings come as a set of 2.
        - RETURNS vs WARRANTIES: These are different things. Returns are for items you want to send back (wrong size, changed mind, etc.). Warranties are for defective/broken items.
        - SHIPPING: We ship internationally! Free shipping on orders over $99 is for Canadian orders only. International shipping costs are calculated at checkout.
        
        FORMATTING REQUIREMENTS:
        - Your response MUST be formatted in HTML
        - Start with <p> for the first paragraph
        - Use <ul> and <li> for lists of features or items
        - Use <ol> and <li> for numbered steps or sequences
        - Use <strong> to emphasize important words
        - Use <br> for line breaks within paragraphs
        - Do not use any other HTML tags
        - Do not include any HTML comments or meta tags
        - Do not include any CSS or JavaScript
        - Do not include any HTML attributes
        - Example format:
          <p>Available in sizes 9 and 12 - perfect for getting that dialed fit!</p>
          <ul>
            <li>Bombproof 7-ply maple construction</li>
            <li>Rad graphic that won't fade</li>
          </ul>
          <p>You're gonna be stoked on this setup!</p>

        ${warrantyType ? `
        WARRANTY AND SUPPORT HANDLING:
        ${warrantyType === 'info' ? `
        - Use this EXACT format:
          <p>Totally depends on the brand. Most have some kind of warranty, but it's best to check with the brand directly for the lowdown. We've also got a <a href="https://sanctionsnow.com/pages/returns-warranty">Returns & Warranty page</a> if you wanna peep that too.</p>
          <p>Pro tip: hang onto your receipt — just in case. Stay stoked!</p>
        ` : warrantyType === 'claim' ? `
        - Use this EXACT format:
          <p>Ah bummer, sorry to hear that!</p>
          <p>Good news though — you can start a warranty claim by heading to our <a href="https://sanctionsnow.com/pages/returns-warranty#warranty-info">Returns & Warranty page</a>.</p>
          <p>Just scroll down and click the <strong>"Start A Warranty Claim"</strong> button. We'll get you sorted fast!</p>
        ` : ''}` : ''}
        
        Question: ${message}`;

        const response = await fetch(`${CHAT_CONFIG.apiProxy.baseUrl}${CHAT_CONFIG.apiProxy.endpoints.openai}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            model: "gpt-4o-mini",
            messages: [
              {
                role: "system",
                content: systemPrompt
              },
              {
                role: "user",
                content: message
              }
            ]
          })
        });

        if (!response.ok) {
          const error = await response.json();
          console.error('OpenAI Proxy Error:', error);
          return null;
        }

        const proxyResponse = await response.json();
        
        // Handle proxy response structure
        if (!proxyResponse.success) {
          const error = proxyResponse.error;
          
          // Handle specific error types
          if (error?.includes('insufficient_quota')) {
            console.log('OpenAI quota exceeded, disabling for this session');
            return null;
          }
          
          if (proxyResponse.status === 429) { // Rate limit
            console.log('OpenAI rate limit reached');
            this.lastOpenAICall = Date.now(); // Update rate limit timestamp
            return null;
          }
          
          // Log other errors for debugging
          console.error('OpenAI error:', error || 'Unknown error');
          throw new Error(error || 'OpenAI request failed');
        }

        const data = proxyResponse.data;
        if (!data.choices?.[0]?.message?.content) {
          console.log('OpenAI invalid response format');
          return null;
        }

        let content = data.choices[0].message.content;
        
        // Log the raw response for debugging
        console.log('Raw OpenAI response:', content);
        
        // Process the content to ensure proper HTML formatting
        const processContent = (text) => {
          // First, convert any markdown-style formatting to HTML
          text = this.convertMarkdownToHTML(text);
          
          // First, check if the text is already properly formatted HTML
          if (text.includes('<p>') || text.includes('<ul>') || text.includes('<ol>') || text.includes('<table>')) {
            return text;
          }

          // Split the text into paragraphs
          const paragraphs = text.split('\n\n').filter(p => p.trim());
          
          // Process each paragraph
          return paragraphs.map(paragraph => {
            // Check if it's a list
            if (paragraph.trim().startsWith('-') || paragraph.trim().startsWith('*')) {
              const items = paragraph.split('\n')
                .map(line => line.trim())
                .filter(line => line.startsWith('-') || line.startsWith('*'))
                .map(line => `<li>${line.substring(1).trim()}</li>`)
                .join('');
              return `<ul>${items}</ul>`;
            }
            
            // Check if it's a numbered list
            if (/^\d+\./.test(paragraph.trim())) {
              const items = paragraph.split('\n')
                .map(line => line.trim())
                .filter(line => /^\d+\./.test(line))
                .map(line => `<li>${line.replace(/^\d+\.\s*/, '').trim()}</li>`)
                .join('');
              return `<ol>${items}</ol>`;
            }

            // Check if it's a table
            if (paragraph.includes('|')) {
              const rows = paragraph.split('\n')
                .map(line => line.trim())
                .filter(line => line.includes('|'));
              
              if (rows.length >= 4) { // Need at least header + 3 data rows
                const headerRow = rows[0].split('|')
                  .map(cell => cell.trim())
                  .filter(cell => cell)
                  .map(cell => `<th>${cell}</th>`)
                  .join('');
                
                const dataRows = rows.slice(1)
                  .map(row => row.split('|')
                    .map(cell => cell.trim())
                    .filter(cell => cell)
                    .map(cell => `<td>${cell}</td>`)
                    .join(''))
                  .map(row => `<tr>${row}</tr>`)
                  .join('');
                
                return `<table><tr>${headerRow}</tr>${dataRows}</table>`;
              }
            }
            
            // Regular paragraph
            return `<p>${paragraph.trim()}</p>`;
          }).join('');
        };

        // Process the content
        content = processContent(content);
        
        // Log the processed HTML for debugging
        console.log('Processed HTML:', content);
        
        // Validate response quality
        if (!this.isValidResponse(content)) {
          console.log('OpenAI response failed quality check');
          return null;
        }

        this.lastOpenAICall = Date.now();
        return content;
      } catch (error) {
        console.error('OpenAI Chat Error:', error);
        return null;
      }
    }
  
    async tryGeminiChat(message) {
      try {
        // Check if this is a warranty-related query (but not a return query)
        const warrantyType = !this.isReturnQuery(message) ? this.isWarrantyOrSupportQuery(message) : false;

        // Check if the question is about sizing and if sizing specs are available
        const isSizeQuestion = message.toLowerCase().includes('size') || 
                             message.toLowerCase().includes('fit') || 
                             message.toLowerCase().includes('measurement');
        const hasSizingSpecs = this.productInfo.details?.includes('Sizing Specs');
        
        // Check if this is a truck sizing question
        const isTruckSizingQuestion = message.toLowerCase().includes('truck') && 
                                    (message.toLowerCase().includes('size') || 
                                     message.toLowerCase().includes('width') || 
                                     message.toLowerCase().includes('fit') ||
                                     message.toLowerCase().includes('choose') ||
                                     message.toLowerCase().includes('recommend'));
        
        const prompt = `As a young, friendly skater/snowboarder who works at Sanction skateshop, answer this question about a ${this.productType} titled "${this.productTitle}".
        You're super passionate about riding and love helping people find the perfect setup. You've been skating/riding for years and know all the gear inside out. You have a casual, fun personality and love sharing your stoke for the sport.

        Here is the available product information:
        ${this.productInfo.description ? `\nDescription: ${this.productInfo.description}` : ''}
        ${this.productInfo.details ? `\nDetails: ${this.productInfo.details}` : ''}
        ${this.productInfo.specifications ? `\nSpecifications: ${this.productInfo.specifications}` : ''}
        
        ${isSizeQuestion && hasSizingSpecs ? `\nIMPORTANT: The user has asked about sizing, and there is a "Sizing Specs" section available on the product page. Direct them to check the "Sizing Specs" section just below this chat window for detailed measurements and fit information.` : ''}
        
        ${isTruckSizingQuestion ? `
        SKATEBOARD TRUCK SIZING GUIDANCE:
        
        When customers ask which skateboard trucks they should get, help them choose the right skateboard truck size based on deck width and truck brand.
        
        Rules:
        1. If the user is on a skateboard truck product page (or the product name includes "truck"):
           • Check the product name to identify the brand.
           • Return sizing guidance only for the matching brand.
        
        2. If the user is on a skateboard deck or wheels product page and asks about truck sizing:
           • Return the full sizing chart for all brands.
           • If they mention a specific truck brand in the question, return only that brand's sizing.
        
        3. If the brand cannot be determined:
           • Ask the user for their deck width and preferred truck brand before making a recommendation.
        
        Known truck brands and sizing guidelines:
        
        Independent:
        • 7.4"-7.8": 129
        • 7.8"-8.2": 139
        • 8.2"-8.375": 144
        • 8.375"-8.6": 149
        • 8.6"-9.0": 159
        • 9.0"-9.5": 169
        • 9.5"-10.5"+: 215
        
        Thunder:
        • 7.4"-7.9": 145
        • 7.9"-8.12": 147
        • 8.12"-8.38": 148
        • 8.38"-8.62": 149
        • 8.62"-8.85": 151
        • 8.9"+: 161
        • 9.5"+: 181
        
        Ace AF1:
        • 7.25"-8.0": 22
        • 8.0"-8.25": 33
        • 8.25"-8.5": 44
        • 8.5"-8.75": 55
        • 8.75"-9.0": 60
        • 9.0"-9.25": 66
        • 9.25"+: 77
        • 10.0"+: 80
        
        Ace Classic:
        • 7.5"-7.875": 22
        • 7.875"-8.25": 33
        • 8.25"-8.75": 44
        • 8.75"-9.125": 55
        • 9.125"-9.75": 66
        
        Venture:
        • 7.9"-8.1": 5.2
        • 8.1"-8.3": 5.6
        • 8.4"-8.6": 5.8
        • 8.62"+: 6.1
        
        Lurpiv:
        • 7.75"-8.125": 140
        • 8.25"-8.5": 145
        • 8.5"-8.75": 150
        • 8.75"+: 160
        
        Slappy:
        • 7.5"-8.0": 7.75"
        • 7.75"-8.25": 8"
        • 8.0"-8.5": 8.25"
        • 8.25"-8.75": 8.5"
        • 8.5"-9.0": 8.75"
        • 9.5"-10.5": 10"
        • 10.5"-11.5": 11"
        
        Royal:
        • 7.75"-8.25": 139
        • 8.0"-8.5": 144
        • 8.25"-8.75": 149
        • 8.5"+: 159
        ` : ''}
        
        CRITICAL RULES - DO NOT BREAK THESE:
        - ABSOLUTELY NEVER mention the product name, brand, or model in your response
        - NEVER use phrases like "this product", "this item", "these shoes", "this deck", etc.
        - NEVER start with "The [product name] is..." or similar constructions
        - ALWAYS talk about features and details directly without any product references
        - NEVER use any greeting-like language (no "Hey", "Hi", "Yo", "What's up", etc.)
        - NEVER address the user directly
        - Example of what NOT to do:
          ❌ "The New Balance Numeric Reynolds 933 Black Olive is available in sizes 9 and 12"
          ❌ "This deck has a perfect concave for street riding"
          ❌ "These bindings are super responsive"
          ❌ "Hey there! Let me tell you about this product..."
          ❌ "Yo! I've been riding these for years..."
        - Example of what to do:
          ✅ "Available in sizes 9 and 12 - perfect for getting that dialed fit!"
          ✅ "The concave is perfect for street riding - you'll be popping ollies like butter!"
          ✅ "Super responsive bindings that'll have you carving like a dream"
        
        OTHER IMPORTANT RULES:
        - Keep responses short and to the point - aim for 2-3 sentences max
        - Use skate/snowboard slang naturally: "stoked", "rad", "send it", "cruise", "gnarly", "buttery", "pop", "shred", etc.
        - Use emojis VERY occasionally - only when it feels natural and adds to the message
        - DO NOT use emojis in every response
        - Share your genuine excitement about riding and the gear
        - Make the customer feel stoked to ride
        - Keep it fun and energetic but brief
        - Use the product info to give helpful, accurate advice
        - Share your personal experience when relevant
        - Throw in a quick fun comment or joke sometimes
        - Avoid any corporate or formal language
        - Stay positive and enthusiastic
        - Make the customer feel welcome and excited
        - DO NOT start responses with greetings like "Hey" or "Hi"
        - Format your response in simple HTML
        - Use <strong> for emphasis
        - Use <ul> and <li> for lists
        - Use <br> for line breaks
        - Keep formatting clean and minimal
        
        SPECIFIC PRODUCT KNOWLEDGE:
        - SKATEBOARD TRUCKS: Always sold in pairs (set of 2). If someone asks if they're sold individually or as pairs, always clarify they come as a pair.
        - SKATEBOARD WHEELS: Always sold as a set of four (4). If someone asks about wheel quantities, pricing, or individual wheels, always clarify that wheels come as a set of 4.
        - SNOWBOARD BINDINGS: Always sold as a set of two (2). If someone asks about binding quantities, pricing, or individual bindings, always clarify that bindings come as a set of 2.
        - RETURNS vs WARRANTIES: These are different things. Returns are for items you want to send back (wrong size, changed mind, etc.). Warranties are for defective/broken items.
        - SHIPPING: We ship internationally! Free shipping on orders over $99 is for Canadian orders only. International shipping costs are calculated at checkout.

        ${warrantyType ? `
        WARRANTY AND SUPPORT HANDLING:
        ${warrantyType === 'info' ? `
        - Use this EXACT format:
          <p>Totally depends on the brand. Most have some kind of warranty, but it's best to check with the brand directly for the lowdown. We've also got a <a href="https://sanctionsnow.com/pages/returns-warranty">Returns & Warranty page</a> if you wanna peep that too.</p>
          <p>Pro tip: hang onto your receipt — just in case. Stay stoked!</p>
        ` : warrantyType === 'claim' ? `
        - Use this EXACT format:
          <p>Ah bummer, sorry to hear that!</p>
          <p>Good news though — you can start a warranty claim by heading to our <a href="https://sanctionsnow.com/pages/returns-warranty#warranty-info">Returns & Warranty page</a>.</p>
          <p>Just scroll down and click the <strong>"Start A Warranty Claim"</strong> button. We'll get you sorted fast!</p>
        ` : ''}` : ''}
        
        Question: ${message}`;
  
        const response = await fetch(`${CHAT_CONFIG.apiProxy.baseUrl}${CHAT_CONFIG.apiProxy.endpoints.gemini}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            contents: [{
              parts: [{
                text: prompt
              }]
            }],
            generationConfig: {
              temperature: 0.7,
              topP: 0.8,
              topK: 40
            }
          })
        });
  
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.log('Gemini error details:', errorData);
          return null;
        }
  
        const proxyResponse = await response.json();
        
        // Handle proxy response structure
        if (!proxyResponse.success) {
          console.error('Gemini Proxy Error:', proxyResponse.error);
          return null;
        }
        
        const data = proxyResponse.data;
        if (!data.candidates?.[0]?.content?.parts?.[0]?.text) {
          console.log('Gemini invalid response format:', data);
          return null;
        }
  
        let responseText = data.candidates[0].content.parts[0].text;
        
        // Convert markdown-style formatting to HTML tags
        responseText = this.convertMarkdownToHTML(responseText);
        
        // Clean up formatting
        responseText = responseText
          .replace(/```language=html/gi, '')
          .replace(/```/g, '')
          .replace(/^html\s*/i, '')
          .replace(/\n{2,}/g, '\n')  // Replace multiple newlines with single newline
          .replace(/\s*<br>\s*<br>\s*/g, '<br>')  // Replace double <br> with single
          .replace(/>\s+</g, '><')  // Remove spaces between tags
          .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
          .trim();

        this.lastGeminiCall = Date.now();
        return responseText;
      } catch (error) {
        console.error('Gemini Chat Error:', error);
        return null;
      }
    }
  
    async addMessageToChat(role, content) {
      const { messageDiv, contentWrapper } = this.createMessageElement(role, content);
      
      // Special handling for "Thinking..." message
      if (content === 'Thinking...') {
        messageDiv.appendChild(this.createTypingIndicator());
        this.chatMessages.appendChild(messageDiv);
        this.scrollToMessage(messageDiv);
        return;
      }

      // Clean up any trailing <br> tags
      content = content.replace(/<br>\s*$/, '');
      
      // For user messages, display immediately
      if (role === 'user') {
        contentWrapper.innerHTML = content;
        this.chatMessages.appendChild(messageDiv);
        this.scrollToMessage(messageDiv);
        return;
      }

      // For assistant messages, add typewriter effect
      this.chatMessages.appendChild(messageDiv);
      this.scrollToMessage(messageDiv);
      await this.typewriterEffect(contentWrapper, content);
    }

    scrollToMessage(messageElement) {
      // Get the chat container's dimensions
      const container = this.chatMessages;
      const containerHeight = container.clientHeight;
      const containerScrollTop = container.scrollTop;
      const containerScrollBottom = containerScrollTop + containerHeight;

      // Get the message element's position
      const messageTop = messageElement.offsetTop;
      const messageHeight = messageElement.clientHeight;
      
      // Calculate the ideal scroll position
      const padding = 20;
      const targetScrollTop = Math.min(
        messageTop - padding,
        messageTop + messageHeight - containerHeight + padding
      );

      // Only scroll if the message isn't already fully visible
      const isVisible = messageTop >= containerScrollTop && 
                       messageTop + messageHeight <= containerScrollBottom;
      
      if (!isVisible) {
        // Use requestAnimationFrame for smoother animation
        requestAnimationFrame(() => {
          container.scrollTo({
            top: targetScrollTop,
            behavior: 'smooth'
          });
        });
      }
    }

    async typewriterEffect(element, content) {
      // Create a temporary div to parse HTML content
      const tempDiv = document.createElement('div');
      const cleanContent = content.replace(/`/g, '').trim();
      tempDiv.innerHTML = cleanContent;

      // Clear existing content
      element.innerHTML = '';
      
      // Process nodes with improved performance
      const processNode = async (node, parentElement) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const newElement = document.createElement(node.tagName);
          
          // Copy attributes
          Array.from(node.attributes).forEach(attr => {
            newElement.setAttribute(attr.name, attr.value);
          });
          
          parentElement.appendChild(newElement);
          
          // Process children sequentially for better visual effect
          for (const child of Array.from(node.childNodes)) {
            await processNode(child, newElement);
          }
        } else if (node.nodeType === Node.TEXT_NODE && node.textContent.trim()) {
          const textContainer = document.createElement('span');
          textContainer.style.display = 'inline';
          parentElement.appendChild(textContainer);
          
          // Split text into words while preserving whitespace
          const words = node.textContent.split(/(\s+)/);
          
          // Process each word individually for smoother animation
          for (const word of words) {
            if (word.trim()) {
              // For non-whitespace words, add character by character
              const wordSpan = document.createElement('span');
              wordSpan.style.display = 'inline';
              textContainer.appendChild(wordSpan);
              
              for (const char of word) {
                wordSpan.textContent += char;
                this.adjustScroll(element);
                // Use a consistent delay between characters
                await new Promise(resolve => setTimeout(resolve, this.typewriterDelay));
              }
            } else {
              // For whitespace, add it directly
              textContainer.appendChild(document.createTextNode(word));
            }
          }
        }
      };

      // Process nodes sequentially for better visual effect
      for (const child of Array.from(tempDiv.childNodes)) {
        await processNode(child, element);
      }
    }

    adjustScroll(element) {
      const container = this.chatMessages;
      const messageElement = element.closest('.chat-message');
      const containerHeight = container.clientHeight;
      const messageTop = messageElement.offsetTop;
      const messageBottom = messageElement.offsetTop + messageElement.offsetHeight;
      const currentScroll = container.scrollTop;
      const visibleBottom = currentScroll + containerHeight;
      
      if (messageBottom > visibleBottom) {
        const padding = 20;
        const targetScroll = Math.min(
          messageBottom - containerHeight + padding,
          messageTop - padding
        );
        
        if (Math.abs(targetScroll - currentScroll) > 1) {
          requestAnimationFrame(() => {
            container.scrollTo({
              top: targetScroll,
              behavior: 'smooth'
            });
          });
        }
      }
    }
  
    removeMessage(id) {
      const message = this.chatMessages.querySelector(`[data-message-id="${id}"]`);
      if (message) message.remove();
    }
  
    gatherProductInformation() {
      const info = {
        description: '',
        details: '',
        specifications: ''
      };

      // Helper function to gather content from selectors
      const gatherContent = (selectors) => {
        for (const selector of selectors) {
          const elements = document.querySelectorAll(selector);
          if (elements.length > 0) {
            const content = Array.from(elements)
              .map(el => el.textContent.trim())
              .filter(text => text.length > 0)
              .join('\n\n');
            if (content) return content;
          }
        }
        return '';
      };

      // Gather content for each section
      info.description = gatherContent(CHAT_CONFIG.selectors.description);
      info.details = gatherContent(CHAT_CONFIG.selectors.details);
      info.specifications = gatherContent(CHAT_CONFIG.selectors.specifications);

      // If no description or details found, try to find any product information
      if (!info.description && !info.details) {
        const productSection = document.querySelector('.product-section, .product, #product-area, #MainContent');
        if (productSection) {
          const allTextElements = productSection.querySelectorAll('p, div, span, li');
          const productInfo = Array.from(allTextElements)
            .map(el => el.textContent.trim())
            .filter(text => text.length > 20) // Only include substantial text
            .filter(text => !text.includes('Quantity') && !text.includes('Add to cart')) // Filter out UI elements
            .join('\n\n');
          
          if (productInfo) {
            info.description = productInfo;
          }
        }
      }

      return info;
    }

    // Add response quality check method
    isValidResponse(response) {
      if (!response) return false;
      
      // Check for minimum length
      if (response.length < 10) return false;
      
      // Check for error-like phrases
      const errorPhrases = [
        "error", "invalid", "failed",
        "not available", "try again",
        "cannot process"
      ];
      if (errorPhrases.some(phrase => response.toLowerCase().includes(phrase))) {
        return false;
      }
      
      return true;
    }

    // Add a helper method to convert markdown-style formatting to HTML
    convertMarkdownToHTML(text) {
      // Handle bold text (convert **text** to <strong>text</strong>)
      let result = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      
      // Handle italic text (convert *text* to <em>text</em>) - only if not part of **
      result = result.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>');
      
      return result;
    }

    // Add these utility methods for message rendering
    createMessageElement(role, content) {
      const messageDiv = document.createElement('div');
      messageDiv.className = `chat-message chat-message-${role}`;
      
      const contentWrapper = document.createElement('div');
      contentWrapper.className = 'message-content';
      messageDiv.appendChild(contentWrapper);
      
      return { messageDiv, contentWrapper };
    }

    createTypingIndicator() {
      const typingIndicator = document.createElement('div');
      typingIndicator.className = 'typing-indicator';
      typingIndicator.innerHTML = '<span></span><span></span><span></span>';
      return typingIndicator;
    }

    // Tracking methods
    generateSessionId() {
      // Create a short but unique session ID that includes product information
      const timestamp = Date.now().toString(36).substring(0, 6); // Shorter timestamp (last ~6 chars)
      const random = Math.random().toString(36).substring(2, 5); // Shorter random segment
      
      // Generate a short hash from product URL to ensure different products get different IDs
      const url = window.location.pathname;
      let urlHash = 0;
      for (let i = 0; i < url.length; i++) {
        urlHash = ((urlHash << 5) - urlHash) + url.charCodeAt(i);
        urlHash = urlHash & urlHash; // Convert to 32bit integer
      }
      
      // Combine components for a compact session ID (around 10-12 chars total)
      return `${timestamp}${Math.abs(urlHash).toString(36).substring(0, 2)}${random}`;
    }
    
    checkSession() {
      const now = Date.now();
      
      // Check if user has been idle for too long
      const timeSinceLastActivity = now - this.lastActivityTime;
      if (timeSinceLastActivity >= this.sessionTimeout) {
        console.log(`User idle for ${Math.round(timeSinceLastActivity / 1000)} seconds during checkSession`);
        // Before starting a new session, finalize the current one
        this.finalizeSession();
        
        // Start a new session
        this.sessionId = this.generateSessionId();
        this.sessionStartTime = now;
        this.lastInteractionTime = now;
        this.lastActivityTime = now;
        this.questionCount = 0;
        this.interactionCount = 0;
        this.activeSession = true;
        this.trackedInteractions = [];
        this.finalizedInteractions = new Set();
        return;
      }
      
      // Standard timeout check
      if (now - this.lastInteractionTime > this.sessionTimeout) {
        // Before starting a new session, finalize the current one
        this.finalizeSession();
        
        // Start a new session
        this.sessionId = this.generateSessionId();
        this.sessionStartTime = now;
        this.lastInteractionTime = now;
        this.lastActivityTime = now;
        this.questionCount = 0;
        this.interactionCount = 0;
        this.activeSession = true;
        this.trackedInteractions = [];
        this.finalizedInteractions = new Set();
      }
    }
    
    finalizeSession() {
      if (!this.trackingEnabled || !this.activeSession || this.trackedInteractions.length === 0) {
        return;
      }
      
      // Find the last valid interaction (with a real question and answer)
      let lastValidInteraction = null;
      for (let i = this.trackedInteractions.length - 1; i >= 0; i--) {
        const interaction = this.trackedInteractions[i];
        // Skip placeholder or system messages
        if (interaction.question === 'Session Finalization' || 
            interaction.answer === 'Session ended' ||
            interaction.productTitle === 'Unknown Product') {
          continue;
        }
        lastValidInteraction = interaction;
        break;
      }
      
      // If no valid interaction found, don't finalize
      if (!lastValidInteraction) {
        console.log('No valid interaction found, skipping finalization');
        return;
      }
      
      // Check if we already finalized this session
      const sessionWasFinalized = localStorage.getItem(this.storagePrefix + 'finalized_' + this.sessionId);
      
      // Only finalize if we haven't already finalized this interaction and the session is not already marked as finalized
      if (!sessionWasFinalized && !this.finalizedInteractions.has(lastValidInteraction.id)) {
        this.logInteractionToSheet(lastValidInteraction, true);
        
        // Mark this session as finalized in localStorage to prevent duplicate entries
        // Store with timestamp for expiration
        const markerValue = `true|${Date.now()}`;
        localStorage.setItem(this.storagePrefix + 'finalized_' + this.sessionId, markerValue);
      } else if (sessionWasFinalized) {
        console.log('Skipping finalization - session was already finalized:', this.sessionId);
      }
      
      this.activeSession = false;
    }
    
    checkAndFinalizeSession(forceFinalize = false) {
      if (!this.trackingEnabled || !this.activeSession || this.trackedInteractions.length === 0) {
        return;
      }
      
      // Find the last valid interaction (with a real question and answer)
      let lastValidInteraction = null;
      for (let i = this.trackedInteractions.length - 1; i >= 0; i--) {
        const interaction = this.trackedInteractions[i];
        // Skip placeholder or system messages
        if (interaction.question === 'Session Finalization' || 
            interaction.answer === 'Session ended' ||
            interaction.productTitle === 'Unknown Product') {
          continue;
        }
        lastValidInteraction = interaction;
        break;
      }
      
      // If no valid interaction found, don't finalize
      if (!lastValidInteraction) {
        console.log('No valid interaction found, skipping finalization');
        return;
      }
      
      const now = Date.now();
      const timeSinceLastActivity = now - this.lastActivityTime;
      
      if (forceFinalize || 
          now - this.lastInteractionTime > this.sessionTimeout ||
          timeSinceLastActivity >= this.sessionTimeout) {
        // Only finalize if we haven't already finalized this interaction
        if (!this.finalizedInteractions.has(lastValidInteraction.id)) {
          console.log('Finalizing session due to ' + 
                     (forceFinalize ? 'force finalize' : 
                      timeSinceLastActivity >= this.sessionTimeout ? 'user inactivity' : 'timeout'));
          this.logInteractionToSheet(lastValidInteraction, true);
          this.activeSession = false;
          
          // Also mark this session as finalized in localStorage to prevent duplicate entries
          // Store with timestamp for expiration
          const markerValue = `true|${Date.now()}`;
          localStorage.setItem(this.storagePrefix + 'finalized_' + this.sessionId, markerValue);
        }
      }
    }
    
    logInteractionToSheet(interaction, isFinal = false) {
      // If already logged as final, don't log again
      if (this.finalizedInteractions.has(interaction.id)) {
        return;
      }
      
      // Base data for all interactions
      const baseData = {
        sessionId: interaction.sessionId,
        productTitle: interaction.productTitle,
        pageUrl: interaction.pageUrl,
        question: interaction.question,
        answer: interaction.answer.replace(/<[^>]*>?/gm, ''), // Strip HTML
        timeOfDay: interaction.timeOfDay
      };
      
      // If this is a finalization, we only send the session data for updating
      if (isFinal) {
        const updateData = {
          sessionId: interaction.sessionId,
          sessionDuration: `${this.calculateSessionDuration()} mins`,
          totalQuestions: this.questionCount
        };
        
        // Send to Google Sheet
        this.sendToGoogleSheet(updateData);
        
        // Mark as finalized
        this.finalizedInteractions.add(interaction.id);
        console.log('Finalized interaction:', updateData);
      } else {
        // Send regular interaction data to Google Sheet
        this.sendToGoogleSheet(baseData);
        console.log('Logged interaction:', baseData);
      }
    }
    
    calculateSessionDuration() {
      const duration = Math.max(1, Math.round((this.lastInteractionTime - this.sessionStartTime) / 60000));
      return duration;
    }
    
    sendToGoogleSheet(data) {
      if (!this.sheetUrl) return;
      
      // Send the data to Google Sheet
      fetch(this.sheetUrl, {
        method: 'POST',
        mode: 'no-cors', // This is important for cross-origin requests to Google Apps Script
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }).catch(error => {
        console.error('Error sending data to Google Sheet:', error);
      });
    }

    // New methods for session persistence
    saveSessionToLocalStorage() {
      try {
        if (this.trackedInteractions.length === 0) return; // Don't save if no interactions
        
        const sessionData = {
          sessionId: this.sessionId,
          startTime: this.sessionStartTime,
          lastInteraction: this.lastInteractionTime,
          questionCount: this.questionCount,
          interactionCount: this.interactionCount,
          trackedInteractions: this.trackedInteractions,
          finalizedInteractions: Array.from(this.finalizedInteractions),
          productTitle: this.productTitle,
          pageUrl: window.location.href
        };
        
        localStorage.setItem(this.storagePrefix + 'session', JSON.stringify(sessionData));
        localStorage.setItem(this.storagePrefix + 'timestamp', Date.now());
      } catch (error) {
        console.error('Error saving session to localStorage:', error);
      }
    }
    
    checkForPreviousSession() {
      try {
        const storedTimestamp = localStorage.getItem(this.storagePrefix + 'timestamp');
        const sessionData = localStorage.getItem(this.storagePrefix + 'session');
        
        if (!storedTimestamp || !sessionData) return;
        
        // Parse stored data
        const session = JSON.parse(sessionData);
        const timestamp = parseInt(storedTimestamp);
        const now = Date.now();
        
        // Get the current URL path for comparison
        const currentUrlPath = window.location.pathname;
        const storedUrlPath = session.pageUrl ? new URL(session.pageUrl).pathname : '';
        
        // If stored data is recent enough AND on the same page/product, continue the session
        if (now - timestamp < this.sessionTimeout && 
            currentUrlPath === storedUrlPath && 
            this.productTitle === session.productTitle) {
          // We're within the session timeout and on the same product page, so continue the session
          this.sessionId = session.sessionId;
          this.sessionStartTime = session.startTime;
          this.lastInteractionTime = session.lastInteraction;
          this.questionCount = session.questionCount || 0;
          this.interactionCount = session.interactionCount || 0;
          this.trackedInteractions = session.trackedInteractions || [];
          this.finalizedInteractions = new Set(session.finalizedInteractions || []);
          
          // Restore last tracked Q&A for potential future finalization
          if (this.trackedInteractions.length > 0) {
            const lastInteraction = this.trackedInteractions[this.trackedInteractions.length - 1];
            this.lastTrackedQuestion = lastInteraction.question;
            this.lastTrackedAnswer = lastInteraction.answer;
          }
          
          // Log that we've reconnected to the session
          console.log('Reconnected to existing session:', this.sessionId);
        } else {
          // Session has timed out OR we're on a different page/product
          console.log('Found previous session that needs to be finalized:', session.sessionId);
          
          // Check if we already finalized this session before
          const markerKey = this.storagePrefix + 'finalized_' + session.sessionId;
          const sessionWasFinalized = localStorage.getItem(markerKey);
          
          // Only finalize if there are interactions and we haven't already finalized it
          if (!sessionWasFinalized && session.trackedInteractions && session.trackedInteractions.length > 0) {
            const lastInteraction = session.trackedInteractions[session.trackedInteractions.length - 1];
            const finalizedSet = new Set(session.finalizedInteractions || []);
            
            // Only finalize if not already finalized
            if (!finalizedSet.has(lastInteraction.id)) {
              // Calculate the session duration (from start to last interaction)
              const sessionDuration = Math.max(1, Math.round(
                (lastInteraction.timestamp - session.startTime) / 60000));
              
              // Prepare data for Google Sheet
              const data = {
                sessionId: session.sessionId,
                productTitle: lastInteraction.productTitle || session.productTitle || 'Unknown Product',
                pageUrl: lastInteraction.pageUrl || session.pageUrl,
                question: lastInteraction.question,
                answer: lastInteraction.answer.replace(/<[^>]*>?/gm, ''), // Strip HTML
                timeOfDay: lastInteraction.timeOfDay,
                sessionDuration: `${sessionDuration} mins`,
                totalQuestions: session.questionCount || 0,
                timestamp: new Date().toISOString()
              };
              
              // Log the final interaction with the duration and question count
              this.sendToGoogleSheet(data);
              console.log('Finalized previous session:', data);
              
              // Mark this session as finalized to prevent duplicate entries
              // Store with timestamp for expiration
              const markerValue = `true|${Date.now()}`;
              localStorage.setItem(markerKey, markerValue);
            }
          } else if (sessionWasFinalized) {
            console.log('Skipping finalization - session was already finalized:', session.sessionId);
          }
        }
        
        // Clear the stored session
        localStorage.removeItem(this.storagePrefix + 'session');
        localStorage.removeItem(this.storagePrefix + 'timestamp');
      } catch (error) {
        console.error('Error checking previous session:', error);
        // Clear any potentially corrupted data
        localStorage.removeItem(this.storagePrefix + 'session');
        localStorage.removeItem(this.storagePrefix + 'timestamp');
      }
    }

    // New tracking approach - separate tracking from logging
    trackInteraction(question, answer, isFinal = false) {
      const interactionId = Date.now().toString();
      const interaction = {
        id: interactionId,
        sessionId: this.sessionId,
        timestamp: Date.now(),
        question: question,
        answer: answer,
        productTitle: this.productTitle || 'Unknown Product',
        productId: this.productId || '',
        variantId: this.variantId || '',
        pageUrl: window.location.href,
        timeOfDay: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      
      // Add to our tracking array
      this.trackedInteractions.push(interaction);
      
      // Save the most recent interaction for session handling
      this.lastTrackedQuestion = question;
      this.lastTrackedAnswer = answer;
      this.lastInteractionTime = interaction.timestamp;
      
      // Log to Google Sheet
      this.logInteractionToSheet(interaction, isFinal);
      
      // Also update localStorage
      this.saveSessionToLocalStorage();
      
      return interactionId;
    }

    // Update last activity time when user interacts with the page
    updateLastActivityTime() {
      this.lastActivityTime = Date.now();
    }

    // Start the idle checker interval
    startIdleChecker() {
      // Clear any existing interval
      this.stopIdleChecker();
      
      // Check every minute if the user has been idle for too long
      this.idleCheckInterval = setInterval(() => {
        const now = Date.now();
        const timeSinceLastActivity = now - this.lastActivityTime;
        
        // Log idle time for debugging
        console.log(`Time since last activity: ${Math.round(timeSinceLastActivity / 1000)} seconds`);
        
        // If user has been idle longer than the session timeout, finalize the session
        if (timeSinceLastActivity >= this.sessionTimeout) {
          console.log(`User idle for ${Math.round(timeSinceLastActivity / 1000)} seconds, finalizing session`);
          this.checkAndFinalizeSession(true);
          
          // After finalizing, reset the session
          if (this.activeSession) {
            this.sessionId = this.generateSessionId();
            this.sessionStartTime = now;
            this.lastInteractionTime = now;
            this.lastActivityTime = now;
            this.questionCount = 0;
            this.interactionCount = 0;
            this.trackedInteractions = [];
            this.finalizedInteractions = new Set();
            console.log('Session reset due to inactivity');
          }
        }
      }, 60000); // Check every minute
    }
    
    // Stop the idle checker interval
    stopIdleChecker() {
      if (this.idleCheckInterval) {
        clearInterval(this.idleCheckInterval);
        this.idleCheckInterval = null;
      }
    }

    // Get product ID from the URL if not provided in data attributes
    getProductIdFromUrl() {
      const path = window.location.pathname;
      const matches = path.match(/\/products\/([^\/]+)/);
      if (matches && matches[1]) {
        return matches[1]; // Return the product handle
      }
      return '';
    }

    // Initialize conversion tracking
    initConversionTracking() {
      if (!CHAT_CONFIG.tracking.conversionTracking.enabled) return;
      
      // Check if we already have a session for this product in localStorage
      const storageKey = CHAT_CONFIG.tracking.conversionTracking.storageKey;
      const existingData = JSON.parse(localStorage.getItem(storageKey) || '{}');
      const now = Date.now();
      const attributionWindowMs = CHAT_CONFIG.tracking.conversionTracking.attributionWindow * 24 * 60 * 60 * 1000;
      let foundExistingSession = false;
      
      // Clean up expired sessions first
      Object.keys(existingData).forEach(sessionId => {
        if (existingData[sessionId].expiresAt < now) {
          delete existingData[sessionId];
        }
      });
      
      // Look for an existing session for this product that was created very recently (within 5 minutes)
      // This prevents session reuse across separate visits while still allowing session continuation
      // within the same browsing session
      const recentSessionThreshold = 5 * 60 * 1000; // 5 minutes
      
      if (this.productId) {
        for (const sessionId in existingData) {
          const session = existingData[sessionId];
          // Only reuse if it's the same product AND was created very recently (same browsing session)
          if (session.productId === this.productId && 
              session.expiresAt > now && 
              (now - session.timestamp) < recentSessionThreshold) {
            // Use the existing session ID instead of creating a new one
            this.sessionId = sessionId;
            foundExistingSession = true;
            
            // Update the timestamp and expiration to extend the session
            existingData[sessionId].timestamp = now;
            existingData[sessionId].expiresAt = now + attributionWindowMs;
            
            console.log('Reconnected to recent session for product:', this.productId, 'Session ID:', sessionId);
            break;
          }
        }
      }
      
      // If no recent session was found for this product, create a new one
      if (!foundExistingSession) {
        // Store relevant session information in localStorage for conversion tracking
        const conversionData = {
          sessionId: this.sessionId,
          productId: this.productId,
          productTitle: this.productTitle,
          productType: this.productType,
          variantId: this.variantId,
          timestamp: now,
          expiresAt: now + attributionWindowMs,
          hasInteraction: false // Initialize with no interaction
        };
        
        // Store or update current session data
        existingData[this.sessionId] = conversionData;
        console.log('Created new session for product:', this.productId, 'Session ID:', this.sessionId);
      }
      
      // Save back to localStorage
      localStorage.setItem(storageKey, JSON.stringify(existingData));
      
      // Log for debugging in test mode
      if (CHAT_CONFIG.tracking.conversionTracking.testMode) {
        console.log('ProductChat conversion tracking initialized for session:', this.sessionId);
        console.log('Product:', this.productTitle, 'ID:', this.productId);
      }
    }

    // Add method to update session interaction flag
    updateSessionInteractionFlag() {
      // Skip if conversion tracking is disabled
      if (!CHAT_CONFIG.tracking.conversionTracking.enabled) return;
      
      try {
        // Get existing session data from localStorage
        const storageKey = CHAT_CONFIG.tracking.conversionTracking.storageKey;
        const sessionData = JSON.parse(localStorage.getItem(storageKey) || '{}');
        
        // Check if this session exists
        if (sessionData[this.sessionId]) {
          // Update the hasInteraction flag
          sessionData[this.sessionId].hasInteraction = true;
          
          // Save back to localStorage
          localStorage.setItem(storageKey, JSON.stringify(sessionData));
          
          if (CHAT_CONFIG.tracking.conversionTracking.testMode) {
            console.log(`Updated hasInteraction flag for session: ${this.sessionId}`);
          }
        }
      } catch (error) {
        console.error('Error updating session interaction flag:', error);
      }
    }
  }
  
  // Initialize the chat when the document is ready
  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('.product-chat-container')) {
      new ProductChat();
    }
    
    // Add listeners for Add to Cart buttons if conversion tracking is enabled
    if (CHAT_CONFIG.tracking.conversionTracking.enabled) {
      // Function to extract product data from the page
      const getProductData = () => {
        try {
          // Try to get productId and variantId from different sources
          let productId = '';
          let variantId = '';
          let price = 0;
          
          // Check for a product chat container first
          const chatContainer = document.querySelector('.product-chat-container');
          if (chatContainer) {
            productId = chatContainer.dataset.productId;
            variantId = chatContainer.dataset.variantId;
          }
          
          // If not found, try to extract from URL
          if (!productId) {
            const pathParts = window.location.pathname.split('/products/');
            if (pathParts.length > 1) {
              productId = pathParts[1].split('?')[0];
            }
          }
          
          // Try to get variant ID from URL params
          if (!variantId) {
            const urlParams = new URLSearchParams(window.location.search);
            variantId = urlParams.get('variant') || '';
          }
          
          // Try to get the currently selected variant ID from variant picker
          if (!variantId) {
            const variantInput = document.querySelector('input[name="id"]:checked, select[name="id"] option:checked, input[name="id"][type="hidden"]');
            if (variantInput) {
              variantId = variantInput.value;
            }
          }
          
          // Try to get price from multiple sources in order of reliability
          
          // 1. Try variant picker JSON data (most reliable)
          if (!price) {
            try {
              const variantPickerScripts = document.querySelectorAll('script[type="application/json"]');
              for (const script of variantPickerScripts) {
                try {
                  const data = JSON.parse(script.textContent);
                  if (data.variants && Array.isArray(data.variants)) {
                    let targetVariant = null;
                    
                    // If we have a specific variant ID, find it
                    if (variantId) {
                      targetVariant = data.variants.find(v => v.id.toString() === variantId);
                    }
                    
                    // Otherwise use the first available variant
                    if (!targetVariant) {
                      targetVariant = data.variants.find(v => v.available) || data.variants[0];
                      if (targetVariant) {
                        variantId = targetVariant.id.toString();
                      }
                    }
                    
                    if (targetVariant && targetVariant.price) {
                      // Shopify stores prices in cents, so convert to dollars
                      price = parseFloat(targetVariant.price) / 100;
                      break;
                    }
                  }
                } catch (e) {
                  // Skip invalid JSON
                }
              }
            } catch (e) {
              // Continue to next method
            }
          }
          
          // 2. Try global Shopify product object
          if (!price && window.meta && window.meta.product) {
            const product = window.meta.product;
            productId = productId || product.id.toString();
            
            // Get selected variant if available
            if (product.variants && variantId) {
              const variant = product.variants.find(v => v.id.toString() === variantId);
              if (variant) {
                price = parseFloat(variant.price) / 100; // Convert cents to dollars
              }
            } else if (product.price) {
              price = parseFloat(product.price) / 100;
            }
          }
          
          // 3. Try window.ShopifyAnalytics.meta.product (another common source)
          if (!price && window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
            const product = window.ShopifyAnalytics.meta.product;
            if (product.variants && variantId) {
              const variant = product.variants.find(v => v.id.toString() === variantId);
              if (variant && variant.price) {
                price = parseFloat(variant.price) / 100;
              }
            } else if (product.price) {
              price = parseFloat(product.price) / 100;
            }
          }
          
          // 4. Try to get price from formatted price in variant picker data
          if (!price && variantId) {
            try {
              const variantPickerScripts = document.querySelectorAll('script[type="application/json"]');
              for (const script of variantPickerScripts) {
                try {
                  const data = JSON.parse(script.textContent);
                  if (data.formatted && data.formatted[variantId] && data.formatted[variantId].price) {
                    // This should be already formatted as a currency string like "$440.99"
                    const formattedPrice = data.formatted[variantId].price;
                    const priceMatch = formattedPrice.match(/[\d,]+\.?\d*/);
                    if (priceMatch) {
                      price = parseFloat(priceMatch[0].replace(/,/g, ''));
                      break;
                    }
                  }
                } catch (e) {
                  // Skip invalid JSON
                }
              }
            } catch (e) {
              // Continue to next method
            }
          }
          
          // 5. Try to get price from the current displayed price (last resort)
          if (!price) {
            const priceSelectors = [
              '.price__current',
              '.product__price',
              '.price',
              '.product-price',
              '[data-product-price]',
              '.current-price',
              '.selling-price'
            ];
            
            for (const selector of priceSelectors) {
              const priceElement = document.querySelector(selector);
              if (priceElement) {
                // Try to parse the price text - remove currency symbols and formatting
                const priceText = priceElement.textContent.trim()
                  .replace(/[^0-9.,]/g, '')  // Remove non-numeric characters except . and ,
                  .replace(/,/g, '.');       // Replace commas with dots
                
                const parsedPrice = parseFloat(priceText);
                if (parsedPrice && parsedPrice > 0) {
                  price = parsedPrice;
                  break;
                }
              }
            }
          }
          
          // Get quantity (default to 1)
          let quantity = 1;
          const quantityInput = document.querySelector(
            'input[name="quantity"], [data-quantity-input], .quantity-selector__input'
          );
          
          if (quantityInput) {
            quantity = parseInt(quantityInput.value) || 1;
          }
          
          // Debug logging in test mode
          if (CHAT_CONFIG.tracking.conversionTracking.testMode) {
            console.log('Price extraction debug:', {
              productId,
              variantId,
              price,
              quantity,
              extractionMethod: price > 0 ? 'success' : 'failed'
            });
          }
          
          return {
            product_id: productId,
            variant_id: variantId,
            price: price,
            quantity: quantity
          };
        } catch (error) {
          console.error('Error getting product data:', error);
          return {};
        }
      };
      
      // Setup listeners for all potential Add to Cart buttons
      const setupAddToCartListeners = () => {
        const testMode = CHAT_CONFIG.tracking.conversionTracking.testMode;
        
        // Find all potential Add to Cart buttons
        let addToCartButtons = [];
        CHAT_CONFIG.selectors.addToCartButtons.forEach(selector => {
          const buttons = document.querySelectorAll(selector);
          if (buttons.length > 0) {
            buttons.forEach(button => addToCartButtons.push(button));
          }
        });
        
        if (testMode) {
          console.log(`Found ${addToCartButtons.length} potential Add to Cart buttons`);
        }
        
        // Add event listeners to each button
        addToCartButtons.forEach(button => {
          if (!button.dataset.chatTrackingSetup) {
            button.addEventListener('click', () => {
              if (testMode) {
                console.log('Add to Cart button clicked');
              }
              
              // Get product data
              const productData = getProductData();
              
              if (!productData.product_id) {
                if (testMode) {
                  console.log('Could not determine product ID, cannot track conversion');
                }
                return;
              }
              
              if (testMode) {
                console.log('Product data for conversion:', productData);
              }
              
              // Track the conversion
              window.ProductChatTracking.trackConversion(productData);
            });
            
            // Mark the button as having the event listener set up
            button.dataset.chatTrackingSetup = 'true';
            
            if (testMode) {
              console.log('Add to Cart tracking listener added to:', button);
            }
          }
        });
      };
      
      // Run initial setup
      setupAddToCartListeners();
      
      // Also set up a MutationObserver to handle dynamically added buttons
      const observer = new MutationObserver(mutations => {
        // Check if any mutations add potential Add to Cart buttons
        let shouldRecheck = false;
        
        mutations.forEach(mutation => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach(node => {
              if (node.nodeType === 1) { // ELEMENT_NODE
                // Check if this node or its children might contain an Add to Cart button
                const hasButton = CHAT_CONFIG.selectors.addToCartButtons.some(selector => {
                  return node.matches && node.matches(selector) || 
                         node.querySelector && node.querySelector(selector);
                });
                
                if (hasButton) {
                  shouldRecheck = true;
                }
              }
            });
          }
        });
        
        // If we detected changes that might include new buttons, run the setup again
        if (shouldRecheck) {
          setupAddToCartListeners();
        }
      });
      
      // Start observing the document
      observer.observe(document.body, { 
        childList: true, 
        subtree: true 
      });
    }
  });

  // Add a function to simulate an Add to Cart conversion
  window.simulateAddToCart = function(productId) {
    console.log('Simulating Add to Cart conversion...');
    
    // If no productId provided, check if there's one on the page
    if (!productId) {
      // Try to get from container data attribute
      productId = document.querySelector('.product-chat-container')?.dataset.productId;
      
      // If not found, try parsing from URL
      if (!productId) {
        const pathParts = window.location.pathname.split('/products/');
        if (pathParts.length > 1) {
          productId = pathParts[1].split('?')[0];
        }
      }
    }
    
    if (!productId) {
      console.log('❌ Cannot proceed - no product ID available');
      return false;
    }
    
    console.log(`Using product ID for Add to Cart: ${productId}`);
    
    // Create a simulated product
    const simulatedProduct = {
      product_id: productId,
      variant_id: 'test-variant',
      price: 99.99,
      quantity: 1
    };
    
    console.log('Simulating Add to Cart for:', simulatedProduct);
    
    // Track the conversion
    const result = window.ProductChatTracking.trackConversion(simulatedProduct);
    
    console.log('Add to Cart tracking result:', result);
    
    if (result) {
      console.log('✅ Add to Cart simulation successful!');
      console.log('If Google Sheet is updating correctly, you should see this conversion attributed to the session with product ID:', productId);
    } else {
      console.log('❌ Add to Cart simulation failed.');
      console.log('Most likely cause: No session exists for product ID', productId);
      console.log('Try interacting with the chat on this product page first.');
    }
    
    return result;
  };

  // Add a function to debug price extraction
  window.debugPriceExtraction = function() {
    console.log('=== PRICE EXTRACTION DEBUG ===');
    
    // First, check what we can see on the page
    console.log('Current URL:', window.location.href);
    
    // Check for product chat container
    const chatContainer = document.querySelector('.product-chat-container');
    if (chatContainer) {
      console.log('Chat container data:', {
        productId: chatContainer.dataset.productId,
        variantId: chatContainer.dataset.variantId,
        productTitle: chatContainer.dataset.productTitle
      });
    } else {
      console.log('No chat container found');
    }
    
    // Check for variant picker scripts
    const variantPickerScripts = document.querySelectorAll('script[type="application/json"]');
    console.log(`Found ${variantPickerScripts.length} JSON scripts`);
    
    variantPickerScripts.forEach((script, index) => {
      try {
        const data = JSON.parse(script.textContent);
        if (data.variants) {
          console.log(`Script ${index + 1} - Product variants found:`, data.variants.length);
          if (data.variants.length > 0) {
            const firstVariant = data.variants[0];
            console.log(`  First variant:`, {
              id: firstVariant.id,
              title: firstVariant.title,
              price: firstVariant.price,
              priceInDollars: firstVariant.price / 100,
              available: firstVariant.available
            });
          }
        }
        if (data.formatted) {
          console.log(`Script ${index + 1} - Formatted prices:`, Object.keys(data.formatted).length, 'variants');
          const firstFormatted = Object.values(data.formatted)[0];
          if (firstFormatted) {
            console.log(`  First formatted:`, firstFormatted);
          }
        }
      } catch (e) {
        console.log(`Script ${index + 1} - Not variant data or invalid JSON`);
      }
    });
    
    // Check global objects
    if (window.meta && window.meta.product) {
      console.log('window.meta.product found:', {
        id: window.meta.product.id,
        price: window.meta.product.price,
        variants: window.meta.product.variants?.length || 0
      });
    } else {
      console.log('window.meta.product not found');
    }
    
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      console.log('ShopifyAnalytics.meta.product found:', window.ShopifyAnalytics.meta.product);
    } else {
      console.log('ShopifyAnalytics.meta.product not found');
    }
    
    // Check price elements on page
    const priceSelectors = [
      '.price__current',
      '.product__price', 
      '.price',
      '.product-price',
      '[data-product-price]',
      '.current-price',
      '.selling-price'
    ];
    
    console.log('Price elements found on page:');
    priceSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      if (elements.length > 0) {
        Array.from(elements).forEach((el, index) => {
          console.log(`  ${selector} [${index}]:`, el.textContent.trim());
        });
      }
    });
    
    // Test the actual extraction function
    console.log('=== TESTING PRICE EXTRACTION ===');
    if (typeof getProductData === 'function') {
      const result = getProductData();
      console.log('getProductData() result:', result);
    } else {
      console.log('getProductData function not available - testing inline');
      
      // Run a simplified version of the extraction logic
      let productId = '';
      let variantId = '';
      let price = 0;
      
      // Get product ID from URL
      const pathParts = window.location.pathname.split('/products/');
      if (pathParts.length > 1) {
        productId = pathParts[1].split('?')[0];
      }
      
      // Try variant picker JSON
      variantPickerScripts.forEach(script => {
        try {
          const data = JSON.parse(script.textContent);
          if (data.variants && data.variants.length > 0 && !price) {
            const variant = data.variants[0];
            price = parseFloat(variant.price) / 100;
            variantId = variant.id.toString();
            console.log('Price extracted from JSON variant data:', price);
          }
        } catch (e) {}
      });
      
      console.log('Manual extraction result:', { productId, variantId, price });
    }
    
    console.log('=== END DEBUG ===');
  };

  // Add a function to toggle test mode
  window.toggleTestMode = function(enable) {
    if (enable === undefined) {
      enable = !CHAT_CONFIG.tracking.conversionTracking.testMode;
    }
    
    CHAT_CONFIG.tracking.conversionTracking.testMode = enable;
    console.log(`Test mode ${enable ? 'ENABLED' : 'DISABLED'}`);
    console.log('You can now test add to cart conversions and see detailed logging.');
    
    if (enable) {
      console.log('Available test functions:');
      console.log('- debugPriceExtraction() - Debug price extraction');
      console.log('- simulateAddToCart() - Simulate add to cart conversion');
      console.log('- testProductConversion(productId) - Test specific product conversion');
      console.log('- window.ProductChatTracking.getSessionData() - View session data');
    }
    
    return enable;
  };